// ES module (see shared/i18n/index.js's own header comment). Strings used
// only by admin/ (sales pricing workspace + admin console) — split out of
// the old monolithic shared/i18n/strings.js so portal/ stops shipping them;
// see shared/i18n/common.js's header comment for the full story and
// shared/i18n/admin.js's sibling shared/i18n/portal.js for the other half.
//
// Composed with shared/i18n/common.js by admin/src/i18n.js. Every id below
// keeps its ORIGINAL value unchanged from strings.js — this is a pure move,
// not a rewrite. The Amharic strings were authored by a build agent (no
// native-speaker review pass yet) — see PROGRESS.md Phase 4's "Review
// Amharic strings" item, still open.

const strings = {
  // --- App shell / nav ----------------------------------------------------
  // Split by role — a real bug, not a style choice: this single generic
  // key used to read "Sales pricing workspace" under an admin session too
  // (components/AppShell.jsx rendered it unconditionally). Admin has no
  // single "workspace" the way sales does — the tagline names the whole
  // console instead.
  'app.tagline.sales': { am: 'የሽያጭ የዋጋ አወጣጥ የስራ ቦታ', en: 'Sales pricing workspace' },
  'app.tagline.admin': { am: 'የአስተዳደር ኮንሶል', en: 'Admin console' },
  // Third staff role (migration 025) — see components/AppShell.jsx's own
  // comment on why this isn't a fallback onto either string above.
  'app.tagline.catalogManager': { am: 'የካታሎግ አያያዝ', en: 'Catalog management' },
  'nav.myQueue': { am: 'የእኔ ወረፋ', en: 'My queue' },

  // --- catalog_manager left nav (components/CatalogManagerNav) — Catalog/
  // Categories/Cost management reuse nav.admin.catalog/categories/
  // costManagement above (same screens, same routes, generic copy); only
  // this one nav item is new. ------------------------------------------
  'nav.catalogManager.incomingRequests': { am: 'የገቡ ጥያቄዎች', en: 'Incoming requests' },

  // --- Admin console left nav (components/AdminNav) ------------------------
  'nav.admin.approvalQueue': { am: 'ማጽደቅ ወረፋ', en: 'Approval queue' },
  'nav.admin.allRequests': { am: 'ሁሉም ጥያቄዎች', en: 'All requests' },
  // Nav label only — the page itself (pages/AllProformasPage.jsx, route
  // /all-proformas) keeps its own "All proformas" title/subtitle unchanged.
  // Renamed here rather than adding a second "Quotations" nav item: this
  // page already lists every quotation in every state, and its detail page
  // (AllProformasViewPage.jsx) is where the seal/unseal section now lives —
  // merging was the cleaner of the two options this task's brief offered.
  'nav.admin.allProformas': { am: 'ማቅረቢያዎች', en: 'Quotations' },
  'nav.admin.catalog': { am: 'ካታሎግ', en: 'Catalog' },
  'nav.admin.costManagement': { am: 'የግዢ ዋጋ አያያዝ', en: 'Cost management' },
  'nav.admin.users': { am: 'ተጠቃሚዎች', en: 'Users' },
  'nav.admin.bankAccounts': { am: 'የባንክ ሒሳቦች', en: 'Bank accounts' },
  'nav.admin.categories': { am: 'ምድቦችና ባህሪያት', en: 'Categories & attributes' },
  'nav.admin.settings': { am: 'ቅንብሮች', en: 'Settings' },

  'notFound.backLink': { am: '← ወደ ወረፋ ተመለስ', en: '← Back to queue' },

  // --- Generic states (admin/src/components/ErrorState.jsx's default ids,
  // admin/src/components/PriceReference.jsx's loading label) --------------
  'state.loading': { am: 'በመጫን ላይ...', en: 'Loading...' },
  'state.retry': { am: 'እንደገና ሞክር', en: 'Retry' },
  'state.errorTitle': { am: 'የሆነ ችግር ተፈጥሯል', en: 'Something went wrong' },
  'state.errorGeneric': {
    am: 'ጥያቄውን ማጠናቀቅ አልተቻለም። እባክዎ እንደገና ይሞክሩ።',
    en: 'Could not complete that request. Please try again.',
  },
  'state.unauthenticated': {
    am: 'ክፍለ ጊዜዎ አልቋል። እባክዎ እንደገና ይግቡ።',
    en: 'Your session has ended. Please log in again.',
  },

  // --- Login (sales/admin sign-in — portal has its own separate
  // portal.login.* namespace) -----------------------------------------
  'login.title': { am: 'ወደ ሽያጭ ስራ ቦታ ግባ', en: 'Sign in to the sales workspace' },
  'login.email': { am: 'ኢሜይል', en: 'Email' },
  'login.password': { am: 'የይለፍ ቃል', en: 'Password' },
  'login.submit': { am: 'ግባ', en: 'Sign in' },
  'login.submitting': { am: 'በመግባት ላይ...', en: 'Signing in...' },
  'login.invalidCredentials': {
    am: 'ኢሜይል ወይም የይለፍ ቃል ትክክል አይደለም።',
    en: 'Invalid email or password.',
  },
  'login.accountInactive': {
    am: 'ይህ መለያ ስራ ላይ አይደለም። ከአስተዳዳሪዎ ጋር ይነጋገሩ።',
    en: 'This account is inactive. Contact your administrator.',
  },
  'login.rateLimited': {
    am: 'በጣም ብዙ ያልተሳኩ ሙከራዎች። እባክዎ ቆየት ብለው እንደገና ይሞክሩ።',
    en: 'Too many failed attempts. Try again later.',
  },

  // --- Admin 2FA login step (security-hardening task) ----------------------
  'login.twoFactor.title': {
    am: 'ከማረጋገጫ መተግበሪያዎ የ6-አሃዝ ኮድ ያስገቡ',
    en: 'Enter the 6-digit code from your authenticator app',
  },
  'login.twoFactor.codeLabel': { am: 'የማረጋገጫ ኮድ', en: 'Authentication code' },
  'login.twoFactor.recoveryCodeLabel': { am: 'መልሶ ማግኛ ኮድ', en: 'Recovery code' },
  'login.twoFactor.submit': { am: 'አረጋግጥ', en: 'Verify' },
  'login.twoFactor.submitting': { am: 'በማረጋገጥ ላይ...', en: 'Verifying...' },
  'login.twoFactor.useRecoveryCodeInstead': {
    am: 'በምትኩ መልሶ ማግኛ ኮድ ተጠቀም',
    en: 'Use a recovery code instead',
  },
  'login.twoFactor.useCodeInstead': {
    am: 'በምትኩ የማረጋገጫ ኮድ ተጠቀም',
    en: 'Use an authenticator code instead',
  },
  'login.twoFactor.invalid': { am: 'ኮዱ ትክክል አልነበረም።', en: 'That code was not valid.' },
  'login.twoFactor.rateLimited': {
    am: 'በጣም ብዙ ሙከራዎች። እባክዎ ቆየት ብለው እንደገና ይሞክሩ።',
    en: 'Too many attempts. Try again later.',
  },
  'login.twoFactor.expired': {
    am: 'ክፍለ ጊዜዎ አልቋል። እባክዎ በድጋሚ በኢሜይልና በይለፍ ቃል ይግቡ።',
    en: 'That login attempt expired. Log in with your email and password again.',
  },

  // --- Queue page ----------------------------------------------------------
  'queue.title': { am: 'የእኔ ወረፋ', en: 'My queue' },
  'queue.subtitle': {
    am: 'ለእርስዎ የተመደቡ ጥያቄዎች፣ ካስገቡበት ቀን ጀምሮ በቅደም ተከተል።',
    en: 'Requests assigned to you, oldest first.',
  },
  'queue.col.ref': { am: 'የጥያቄ ቁጥር', en: 'Ref' },
  'queue.col.organization': { am: 'ድርጅት', en: 'Organization' },
  'queue.col.status': { am: 'ሁኔታ', en: 'Status' },
  'queue.col.lines': { am: 'የዕቃ ብዛት', en: 'Lines' },
  'queue.col.neededBy': { am: 'የማድረሻ ቀን', en: 'Required delivery date' },
  'queue.col.assignedAt': { am: 'የተመደበበት ቀን', en: 'Assigned' },
  'queue.emptyTitle': { am: 'ወረፋዎ ባዶ ነው', en: 'Your queue is empty' },
  'queue.emptyBody': {
    am: 'በአሁኑ ጊዜ ለእርስዎ የተመደበ ምንም ጥያቄ የለም። አዲስ ጥያቄ ሲመደብልዎ እዚህ ይታያል።',
    en: 'No requests are assigned to you right now. New assignments will show up here.',
  },
  'queue.errorBody': {
    am: 'የእርስዎን ወረፋ መጫን አልተቻለም።',
    en: 'Could not load your queue.',
  },
  'queue.open': { am: 'ክፈት', en: 'Open' },
  'queue.neededByNone': { am: 'አልተገለጸም', en: 'Not specified' },
  'queue.notPricedYet': { am: 'ገና ዋጋ አልተሰጠውም', en: 'Not priced yet' },

  // Request/quotation status labels (both requests.status and
  // quotations.status values reused, see shared/i18n/index.js's
  // statusLabel()). Admin-only: portal never renders a status value today.
  'status.new': { am: 'አዲስ', en: 'New' },
  'status.assigned': { am: 'የተመደበ', en: 'Assigned' },
  'status.pricing': { am: 'ዋጋ በመስጠት ላይ', en: 'Pricing' },
  'status.pending_approval': { am: 'ማጽደቅ በመጠባበቅ ላይ', en: 'Pending approval' },
  'status.approved': { am: 'ጸድቋል', en: 'Approved' },
  'status.sent': { am: 'ተልኳል', en: 'Sent' },
  'status.rejected': { am: 'ውድቅ ተደርጓል', en: 'Rejected' },
  'status.draft': { am: 'ረቂቅ', en: 'Draft' },
  'status.closed': { am: 'ተዘግቷል', en: 'Closed' },
  'status.cancelled': { am: 'ተሰርዟል', en: 'Cancelled' },
  'status.expired': { am: 'ጊዜው አልፎበታል', en: 'Expired' },
  'status.accepted': { am: 'ተቀባይነት አግኝቷል', en: 'Accepted' },
  'status.superseded': { am: 'በአዲስ ተተክቷል', en: 'Superseded' },
  'status.declined': { am: 'ገዥው ውድቅ አድርጓል', en: 'Declined' },

  // --- Workspace: header -----------------------------------------------
  'workspace.backToQueue': { am: '← ወደ ወረፋ ተመለስ', en: '← Back to queue' },
  'workspace.request': { am: 'ጥያቄ', en: 'Request' },
  'workspace.organization': { am: 'ድርጅት', en: 'Organization' },
  'workspace.organizationFallback': { am: 'ድርጅት #{id}', en: 'Organization #{id}' },
  'workspace.neededBy': { am: 'የሚፈለግ የማድረሻ ቀን', en: 'Required delivery date' },
  'workspace.buyerNote': { am: 'የገዥ ማስታወሻ', en: "Buyer's note" },
  'workspace.purpose': { am: 'ዓላማ', en: 'Purpose' },
  'workspace.source.form': { am: 'ከቅጽ የገባ', en: 'Submitted by form' },
  'workspace.source.excel': { am: 'ከExcel የገባ', en: 'Submitted by Excel' },

  // --- Workspace: our document terms (editable-terms task) ---------------
  // Deliberately NOT reusing "delivery date" anywhere here — workspace.
  // neededBy above is already labelled "Required delivery date" for the
  // BUYER's ask, and this section is OUR commitment, a genuinely different
  // thing. Every label here either says "Our ..." explicitly or is paired
  // with buyerAskedForNote repeating the buyer's own date right next to the
  // delivery field, so the two can be compared side by side instead of
  // mistaken for each other.
  'workspace.terms.sectionTitle': { am: 'የእኛ ቁርጠኝነት', en: 'Our commitment' },
  'workspace.terms.deliveryDaysLabel': {
    am: 'የእኛ የማድረሻ ጊዜ (ቀናት)',
    en: 'Our delivery lead time (days)',
  },
  'workspace.terms.buyerAskedForNote': {
    am: 'ገዥው የጠየቀው፦ {date}',
    en: 'Buyer asked for: {date}',
  },
  'workspace.terms.paymentTermsLabel': { am: 'የክፍያ ውሎች', en: 'Payment terms' },
  'workspace.terms.validUntilLabel': {
    am: 'የዋጋ ማቅረቢያ የሚያበቃበት ቀን',
    en: 'Quote valid until',
  },

  // --- Workspace: lines table --------------------------------------------
  'workspace.linesTitle': { am: 'የዕቃ ዝርዝር', en: 'Line items' },
  'workspace.col.line': { am: '#', en: '#' },
  'workspace.col.description': { am: 'መግለጫ', en: 'Description' },
  'workspace.col.unit': { am: 'መለኪያ', en: 'Unit' },
  'workspace.col.qty': { am: 'ብዛት', en: 'Qty' },
  'workspace.col.product': { am: 'የካታሎግ ምርት', en: 'Catalog product' },
  'workspace.col.unitPrice': { am: 'የነጠላ ዋጋ', en: 'Unit price' },
  'workspace.col.lineTotal': { am: 'ድምር', en: 'Line total' },
  'workspace.col.discountReason': { am: 'የቅናሽ ምክንያት', en: 'Discount reason' },

  'workspace.matched': { am: 'የተጠቆመ ግጥሚያ', en: 'Suggested match' },
  'workspace.matchConfidence': { am: 'የመተማመኛ መጠን፦ {value}%', en: 'Confidence: {value}%' },
  'workspace.notMatched': { am: 'ካታሎግ ውስጥ አልተገኘም', en: 'No catalog match' },
  'workspace.pickProduct': { am: 'ምርት ምረጥ', en: 'Pick product' },
  'workspace.changeProduct': { am: 'ምርት ቀይር', en: 'Change product' },
  'workspace.clearProduct': { am: 'ግንኙነት አንሳ', en: 'Clear link' },
  'workspace.freeText': {
    am: 'ያለ ካታሎግ ምርት፣ በነጻ ጽሁፍ ብቻ ዋጋ ይሰጡ',
    en: 'Price this line free-text, without linking a catalog product',
  },

  'workspace.reference.title': { am: 'የማጣቀሻ ዋጋ', en: 'Price reference' },
  'workspace.reference.list': { am: 'ዝርዝር ዋጋ', en: 'List' },
  'workspace.reference.floor': { am: 'ዝቅተኛ ዋጋ', en: 'Floor' },
  'workspace.reference.tier.government': { am: 'መንግስት', en: 'Government' },
  'workspace.reference.tier.ngo': { am: 'መንግስታዊ ያልሆነ', en: 'NGO' },
  'workspace.reference.tier.corporate': { am: 'ድርጅት', en: 'Corporate' },
  'workspace.reference.tier.retail': { am: 'ችርቻሮ', en: 'Retail' },
  'workspace.reference.none': {
    am: 'ለዚህ ምርት የተመዘገበ የዋጋ እርከን የለም።',
    en: 'No price tier is on file for this product yet.',
  },
  'workspace.reference.noTierPrice': {
    am: 'ለዚህ ደንበኛ እርከን የተመዘገበ ዋጋ የለም።',
    en: 'No price on file for this customer’s tier.',
  },
  'workspace.reference.otherTiers': {
    am: 'ሌሎች እርከኖች ({count})',
    en: 'Other tiers ({count})',
  },
  'workspace.reference.belowFloorHint': {
    am: 'ይህ ዋጋ ከዝቅተኛው ዋጋ በታች ሊሆን ይችላል፤ የቅናሽ ምክንያት ያስፈልግ ይሆናል።',
    en: 'This price may be below the floor for some tiers — a discount reason may be required.',
  },

  // recentQuotes.title stays the longer form for use as a `title`/tooltip
  // attribute (full context on hover); .labelShort/.compactItem/.emptyShort
  // are what actually renders inline in the one-line reference row.
  'workspace.recentQuotes.title': { am: 'ያለፉ ሶስት ዋጋዎች', en: 'Last three quoted prices' },
  'workspace.recentQuotes.labelShort': { am: 'ያለፉት 3፦', en: 'Last 3:' },
  'workspace.recentQuotes.empty': {
    am: 'ለዚህ ምርት ገና ምንም ቀደም ያለ ዋጋ የለም። ይህ ለመጀመሪያ ጊዜ የሚዋጋ ደንበኛ ሊሆን ይችላል።',
    en: 'No previous quotes for this product yet — this may be a first-time customer.',
  },
  'workspace.recentQuotes.emptyShort': { am: 'ቀደም ያለ ዋጋ የለም', en: 'No previous quotes' },
  'workspace.recentQuotes.row': {
    am: '{price} — {organization} — {date}',
    en: '{price} — {organization} — {date}',
  },
  'workspace.recentQuotes.compactItem': { am: '{price} ({date})', en: '{price} ({date})' },

  'workspace.belowFloorRequired': {
    am: 'ዋጋው ከዝቅተኛው ዋጋ በታች ነው። የቅናሽ ምክንያት ያስፈልጋል።',
    en: 'This price is below the floor price. A discount reason is required.',
  },
  'workspace.discountReasonPlaceholder': {
    am: 'ለምሳሌ፦ የብዙ ጊዜ ደንበኛ ቅናሽ',
    en: 'e.g. long-standing customer discount',
  },

  'workspace.footer.subtotalPreview': { am: 'ጊዜያዊ ንዑስ ድምር', en: 'Preview subtotal' },
  'workspace.footer.vatNote': { am: '+15% ተ.እ.ታ በመላክ ላይ ይታከላል', en: '+15% VAT added on submit' },

  'workspace.actions.save': { am: 'አስቀምጥ', en: 'Save' },
  'workspace.actions.saving': { am: 'በማስቀመጥ ላይ...', en: 'Saving...' },
  'workspace.actions.savedAt': { am: 'ተቀምጧል፦ {time}', en: 'Saved {time}' },
  'workspace.actions.submit': { am: 'ለማጽደቅ ላክ', en: 'Submit for approval' },
  'workspace.actions.submitting': { am: 'በመላክ ላይ...', en: 'Submitting...' },
  'workspace.actions.submitDisabledNoLines': {
    am: 'ለመላክ ቢያንስ አንድ የዕቃ መስመር ዋጋ ተሰጥቶ መቀመጥ አለበት።',
    en: 'At least one priced line must be saved before submitting.',
  },
  'workspace.actions.addLine': { am: 'መስመር ጨምር', en: 'Add line' },
  'workspace.actions.removeLine': { am: 'መስመር አስወግድ', en: 'Remove line' },

  'workspace.keyboardHint': {
    am: 'Tab ወደ ታች ወደ ዋጋ አምድ ይወስዳል፣ Enter አሁን ያለውን መስመር ያስቀምጣል፣ Escape ለውጡን ይሰርዛል።',
    en: 'Tab moves down the price column, Enter commits the current line, Escape cancels the edit.',
  },

  'workspace.emptyLinesTitle': { am: 'ገና ምንም የዕቃ መስመር የለም', en: 'No line items yet' },
  'workspace.emptyLinesBody': {
    am: 'እዚህ ገና በራስ-ሰር የገባ ነገር የለም — ይህ የተለመደ ነው። ከታች ባለው ቅጽ ላይ የዕቃ መስመሮችን በእጅ ያስገቡ።',
    en: 'Nothing has been pre-filled here yet — that is normal. Add the line items by hand in the form below.',
  },
  'workspace.errorBody': { am: 'ይህን ጥያቄ መጫን አልተቻለም።', en: 'Could not load this request.' },

  'workspace.savedRowFlash': { am: 'ተቀምጧል', en: 'Saved' },
  'workspace.savedToast': { am: 'ረቂቁ ተቀምጧል', en: 'Draft saved' },
  'workspace.submittedToast': {
    am: 'ማቅረቢያው ለጽ / ማጽደቅ ቀርቧል',
    en: 'Submitted for approval',
  },
  'workspace.mapping.confirmedToast': {
    am: 'ማዛመድ ተረጋግጧል — መስመሮች እየተፈጠሩ ነው',
    en: 'Mapping confirmed — creating line items',
  },

  // --- Sealed-bid tender (migration 020): submit-time checkbox + schedule,
  // and the read-only status shown once a sealed quotation has been sent.
  'workspace.seal.checkboxLabel': {
    am: 'ይህ ማቅረቢያ የታሸገ ጨረታ ይሁን',
    en: 'Seal this proforma (tender bid)',
  },
  'workspace.seal.checkboxHelp': {
    am: 'የታሸገ ማቅረቢያ እስኪከፈት ድረስ ገዥው መስመሮቹን እና ድምር ዋጋውን አያይም — የማጣቀሻ ቁጥር እና ሁኔታ ብቻ ይታያል።',
    en: 'While sealed, the buyer sees only a reference number and status — no line items or totals — until it is unsealed.',
  },
  'workspace.seal.bidOpeningLabel': { am: 'የጨረታ መክፈቻ ጊዜ (አማራጭ)', en: 'Bid opening (optional)' },
  'workspace.seal.bidOpeningHelp': {
    am: 'ጊዜ ባይመረጥም እንኳ፣ በማንኛውም ጊዜ በእጅ መክፈት ይቻላል።',
    en: 'Manual unsealing is always available, whether or not a schedule is set.',
  },
  'workspace.seal.scheduleMustBeFuture': {
    am: 'የጨረታ መክፈቻ ጊዜ ወደፊት መሆን አለበት።',
    en: 'Bid opening time must be in the future.',
  },
  'workspace.seal.sectionTitle': { am: 'የታሸገ ጨረታ', en: 'Sealed tender' },
  'workspace.seal.sealedLabel': { am: 'ታሽጓል፦', en: 'Sealed' },
  'workspace.seal.scheduledBidOpening': { am: 'የታቀደ የጨረታ መክፈቻ', en: 'Scheduled bid opening' },
  'workspace.seal.unsealedNote': {
    am: 'ታሽጎ የነበረው ጨረታ በ{date} ተከፍቷል። ይህ የማይመለስ ነው።',
    en: 'This sealed tender was unsealed on {date}. This cannot be undone.',
  },
  'workspace.seal.pendingSendNote': {
    am: 'ገና ማጽደቅ በመጠበቅ ላይ ነው። ማቅረቢያው ተጽድቆ ለገዥው ከተላከ በኋላ እዚህ ላይ መክፈት ይቻላል።',
    en: 'Still awaiting approval. Unsealing becomes available once this proforma is approved and sent to the buyer.',
  },
  'workspace.seal.unsealNow': { am: 'አሁን ክፈት', en: 'Unseal now' },
  'workspace.seal.unsealing': { am: 'በመክፈት ላይ...', en: 'Unsealing...' },
  'workspace.seal.unsealedToast': { am: 'ማቅረቢያው ተከፍቷል', en: 'Proforma unsealed' },
  'workspace.seal.confirmUnsealTitle': { am: 'ይህ ማቅረቢያ ይከፈት?', en: 'Unseal this proforma?' },
  'workspace.seal.confirmUnsealBody': {
    am: 'ገዥው ወዲያውኑ ሙሉ መስመሮችን እና ድምር ዋጋውን ያያል። ይህ ተመልሶ ሊታሸግ አይችልም።',
    en: 'The buyer will immediately see the full line items and total. It cannot be resealed.',
  },

  // --- Action-feedback toasts + confirm dialogs (useActionFeedback) -----
  'catalog.product.createdToast': { am: 'ምርት ተፈጥሯል', en: 'Product created' },
  'catalog.product.createdWithSkuToast': {
    am: 'ምርት ተፈጥሯል — ኮድ (SKU) {sku}',
    en: 'Product created — SKU {sku}',
  },
  'catalog.product.updatedToast': { am: 'ምርት ተስተካክሏል', en: 'Product updated' },
  'catalog.product.deactivatedToast': { am: 'ምርት ተሰናክሏል', en: 'Product deactivated' },
  'catalog.product.duplicatedToast': { am: 'ምርት ተባዝቷል', en: 'Product duplicated' },
  'catalog.product.duplicatedWithSkuToast': {
    am: 'ምርት ተባዝቷል — ኮድ (SKU) {sku}',
    en: 'Product duplicated — SKU {sku}',
  },
  'catalog.product.featuredToast': { am: 'ምርት ተለይቷል', en: 'Product featured' },
  'catalog.product.unfeaturedToast': { am: 'ከተለዩት ተነስቷል', en: 'Product unfeatured' },
  'catalog.product.featuredOrderSavedToast': { am: 'ቅደም ተከተል ተቀምጧል', en: 'Feature order saved' },
  'catalog.actions.deactivateConfirmTitle': {
    am: '"{name}" ይሰናከል?',
    en: 'Deactivate “{name}”?',
  },
  'catalog.actions.deactivateConfirmBody': {
    am: 'ምርቱ ከካታሎግ ይወጣል። ዳግም ማንቃት በዚህ ማያ ገጽ ላይ የለም።',
    en: 'The product leaves the catalog. There is no reactivate on this screen.',
  },
  // "ይቀጂ" was the wrong stem entirely (reads as the noun "ቅጂ"/"a copy" with
  // a verb prefix bolted on, not a conjugation of ቀዳ "to copy") — found in
  // the native-speaker review pass logged in PROGRESS.md. ይቅዳ is ቀዳ's own
  // jussive ("shall it be copied?"), matching the ይ- question pattern the
  // sibling deactivateConfirmTitle above already uses.
  'catalog.actions.duplicateConfirmTitle': {
    am: '"{name}" ይቅዳ?',
    en: 'Duplicate “{name}”?',
  },
  'catalog.actions.duplicateConfirmBody': {
    am: 'አዲስ ምርት ከዚህ ተመሳሳይ ዝርዝር ይፈጠራል፣ የራሱ አዲስ ኮድ (SKU) ይኖረዋል። ዋጋዎች አይቀዱም።',
    en: 'A new product is created with the same details and its own new SKU. Prices are not copied.',
  },
  'catalog.form.photoUploadedToast': { am: 'ፎቶ ተጭኗል', en: 'Photo uploaded' },
  'catalog.tiers.savedToast': { am: 'የ{tier} ዋጋ ተቀምጧል', en: '{tier} price saved' },
  'costManagement.savedToast': { am: 'ወጪ ተቀምጧል', en: 'Cost saved' },
  'users.createdToast': { am: 'ተጠቃሚ ተፈጥሯል', en: 'User created' },
  'users.deactivatedToast': { am: 'ተጠቃሚ ተሰናክሏል', en: 'User deactivated' },
  'users.actions.deactivateConfirmTitle': {
    am: '"{name}"ን ይሰናክሉ?',
    en: 'Deactivate “{name}”?',
  },
  'users.actions.deactivateConfirmBody': {
    am: 'ተጠቃሚው ከዚያ በኋላ መግባት አይችልም። ይህ በዚህ ማያ ገጽ ላይ መቀልበስ አይቻልም።',
    en: 'The user can no longer sign in. This cannot be undone on this screen.',
  },
  'allRequests.assignedToast': { am: 'ጥያቄ ተመድቧል', en: 'Request assigned' },
  'settings.savedToast': { am: 'ቅንብሮች ተቀምጠዋል', en: 'Settings saved' },
  'settings.twoFactor.enabledToast': {
    am: 'ባለ ሁለት ደረጃ ማረጋገጫ ነቅቷል',
    en: 'Two-factor authentication enabled',
  },
  'settings.twoFactor.disabledToast': {
    am: 'ባለ ሁለት ደረጃ ማረጋገጫ ተሰናክሏል',
    en: 'Two-factor authentication disabled',
  },
  'settings.twoFactor.confirmDisable.title': {
    am: 'ባለ ሁለት ደረጃ ማረጋገጫ ይሰናከል?',
    en: 'Turn off two-factor authentication?',
  },
  'settings.twoFactor.confirmDisable.body': {
    am: 'መለያዎ በይለፍ ቃል ብቻ ይጠበቃል። ማንኛውም ጊዜ እንደገና ማንቃት ይችላሉ።',
    en: 'Your account will be protected by password only. You can turn it back on any time.',
  },
  'error.FEATURED_LIMIT_REACHED': {
    am: 'ከፍተኛው የተለዩ ምርቶች ብዛት ደርሷል።',
    en: 'The maximum number of featured products has been reached.',
  },
  'error.NO_PENDING_SETUP': {
    am: 'ንቁ የማዋቀር ሂደት የለም። እባክዎ እንደገና ይጀምሩ።',
    en: 'There is no setup in progress. Start again.',
  },
  'error.INVALID_CREDENTIALS': {
    am: 'የይለፍ ቃሉ ትክክል አይደለም።',
    en: 'That password is incorrect.',
  },

  // --- Workspace: buyer attachment panel (components/AttachmentPanel) -----
  // The buyer's uploaded file(s). A salesperson reads the BOQ / PDF while
  // pricing; the Amharic here has had no native-speaker review pass yet
  // (see PROGRESS.md Phase 4).
  'workspace.attachment.title': { am: 'የገዥ ፋይል', en: "Buyer's file" },
  'workspace.attachment.kind.spreadsheet': { am: 'ስፕሬድሺት', en: 'spreadsheet' },
  'workspace.attachment.kind.pdf': { am: 'PDF', en: 'PDF' },
  'workspace.attachment.kind.other': { am: 'ፋይል', en: 'file' },
  'workspace.attachment.view': { am: 'ክፈት', en: 'View' },
  'workspace.attachment.hide': { am: 'ዝጋ', en: 'Hide' },
  'workspace.attachment.download': { am: 'አውርድ', en: 'Download' },
  'workspace.attachment.sizeKb': { am: '{value} ኪባ', en: '{value} KB' },
  'workspace.attachment.sizeMb': { am: '{value} ሜባ', en: '{value} MB' },
  'workspace.attachment.unreadable': {
    am: 'ይህን ፋይል በራስ-ሰር ማንበብ አልተቻለም። ከፍተው በእጅ ዋጋ ይስጡ — ፋይሉ ከላይ ለማውረድ ዝግጁ ነው።',
    en: "We couldn't read this file automatically — open it and price by hand. The file is still available to download above.",
  },
  'workspace.attachment.needsMapping': {
    am: 'ይህ ፋይል ወደ የዕቃ መስመሮች ከመቀየሩ በፊት የአምድ ማዛመድ ያስፈልገዋል — ከታች ያዘጋጁ።',
    en: 'This file needs its columns mapped before it becomes line items — set that up below.',
  },

  // --- Workspace: column-mapping panel (components/UploadMappingPanel) ----
  'workspace.mapping.title': { am: 'የአምድ ማዛመድ', en: 'Column mapping' },
  'workspace.mapping.intro': {
    am: 'የገዥን ፋይል አምዶች ከመግለጫ፣ ብዛትና መለኪያ ጋር ያዛምዱ። ከታች ያለው ቅድመ-እይታ የሚፈጠሩትን መስመሮች ያሳያል።',
    en: "Match the buyer's file columns to description, quantity, and unit. The preview shows exactly which rows will become line items.",
  },
  'workspace.mapping.creating': {
    am: 'የዕቃ መስመሮች እየተፈጠሩ ነው…',
    en: 'Creating line items…',
  },
  'workspace.mapping.zeroRowsHint': {
    am: 'የመረጧቸው አምዶች ምንም የዕቃ መስመር አላመነጩም። አምዶቹን ወይም የራስጌ ረድፉን አስተካክለው እንደገና ያረጋግጡ።',
    en: 'The columns you chose produced no line items. Adjust the columns or the header row and confirm again.',
  },
  'workspace.mapping.gridError': {
    am: 'የፋይሉን ረድፎች መጫን አልተቻለም።',
    en: 'Could not load the file rows.',
  },
  'workspace.mapping.fileUnreadable': {
    am: 'ይህ ፋይል ሊነበብ አልቻለም። ከላይ አውርደው በእጅ ዋጋ ይስጡ።',
    en: 'This file could not be read. Download it above and price by hand.',
  },
  'workspace.mapping.col.row': { am: 'ረድፍ', en: 'Row' },
  'workspace.mapping.col.becomes': { am: 'የሚሆነው', en: 'Becomes' },
  'workspace.mapping.columnOption': {
    am: 'አምድ {letter} — "{headerText}"',
    en: 'Column {letter} — "{headerText}"',
  },
  'workspace.mapping.columnOptionBlank': {
    am: 'አምድ {letter} (ባዶ)',
    en: 'Column {letter} (blank)',
  },
  'workspace.mapping.columnNone': { am: '— የለም —', en: '— none —' },
  'workspace.mapping.headerRowLabel': { am: 'የራስጌ ረድፍ', en: 'Header row' },
  'workspace.mapping.headerRowNone': { am: '— ምረጥ —', en: '— choose —' },
  'workspace.mapping.descriptionLabel': { am: 'የመግለጫ አምድ', en: 'Description column' },
  'workspace.mapping.description2Label': {
    am: 'ሁለተኛ የመግለጫ አምድ (አማራጭ)',
    en: 'Second description column (optional)',
  },
  'workspace.mapping.description2Hint': {
    am: 'የዕቃውን ስም እና ዝርዝር መግለጫ በሁለት የተለያዩ አምዶች ለከፈለ ፋይል።',
    en: 'For a file that splits the item name and its specification across two columns.',
  },
  'workspace.mapping.quantityLabel': { am: 'የብዛት አምድ', en: 'Quantity column' },
  'workspace.mapping.unitLabel': { am: 'የመለኪያ አምድ (አማራጭ)', en: 'Unit column (optional)' },
  'workspace.mapping.skuLabel': { am: 'ኮድ (SKU) ዓምድ (አማራጭ)', en: 'SKU/Code column (optional)' },
  'workspace.mapping.skuHint': {
    am: 'ካለ፣ ከዝርዝሩ ጋር በትክክል ለማዛዘን ያገለግላል።',
    en: 'Used to match catalog items exactly, when present.',
  },
  'workspace.mapping.becomes': {
    am: '→ {qty} {unit} — {description}',
    en: '→ {qty} {unit} — {description}',
  },
  'workspace.mapping.becomesNoUnit': {
    am: '→ {qty} — {description}',
    en: '→ {qty} — {description}',
  },
  'workspace.mapping.skip.blankRow': { am: 'ባዶ ረድፍ', en: 'blank row' },
  'workspace.mapping.skip.missingDescription': { am: 'መግለጫ የለም', en: 'no description' },
  'workspace.mapping.skip.descriptionTooLong': { am: 'መግለጫ በጣም ረጅም', en: 'description too long' },
  'workspace.mapping.skip.missingQuantity': { am: 'ብዛት የለም', en: 'no quantity' },
  'workspace.mapping.confirm': {
    am: 'አረጋግጥ — {count} መስመሮች ይፍጠር',
    en: 'Confirm — create {count} lines',
  },
  'workspace.mapping.confirming': { am: 'በማረጋገጥ ላይ…', en: 'Confirming…' },

  // --- Product picker -------------------------------------------------
  'picker.searchPlaceholder': {
    am: 'በስም፣ ብራንድ ወይም ሞዴል ፈልግ...',
    en: 'Search by name, brand, or model...',
  },
  'picker.noResults': { am: 'ምንም ውጤት አልተገኘም', en: 'No results found' },
  'picker.loading': { am: 'እየፈለገ ነው...', en: 'Searching...' },
  'picker.tierRow': {
    am: '{tier} — ዝርዝር {list} / ዝቅተኛ {floor}',
    en: '{tier} — list {list} / floor {floor}',
  },
  'picker.cancel': { am: 'ይቅር', en: 'Cancel' },

  // --- Admin approval queue (pages/ApprovalQueuePage) ---------------------
  'approvalQueue.title': { am: 'ማጽደቅ ወረፋ', en: 'Approval queue' },
  'approvalQueue.subtitle': {
    am: 'ማጽደቅ የሚጠብቁ የዋጋ ማቅረቢያዎች፣ ቀድመው የገቡት አስቀድመው ይታያሉ።',
    en: 'Quotations awaiting a decision, oldest first.',
  },
  'approvalQueue.col.ref': { am: 'የጥያቄ ቁጥር', en: 'Ref' },
  'approvalQueue.col.organization': { am: 'ድርጅት', en: 'Organization' },
  'approvalQueue.col.pricedBy': { am: 'ዋጋ የሰጠው', en: 'Priced by' },
  'approvalQueue.col.submittedAt': { am: 'የገባበት ቀን', en: 'Submitted' },
  'approvalQueue.col.total': { am: 'ጠቅላላ ድምር', en: 'Total' },
  'approvalQueue.emptyTitle': { am: 'ማጽደቅ የሚጠብቅ የለም', en: 'Nothing awaiting approval' },
  'approvalQueue.emptyBody': {
    am: 'በአሁኑ ጊዜ ለማጽደቅ የገባ ምንም የዋጋ ማቅረቢያ የለም። ሽያጭ ሰራተኞች ሲያስገቡ እዚህ ይታያል።',
    en: 'No quotations are waiting on a decision right now. New submissions will show up here.',
  },
  'approvalQueue.errorBody': {
    am: 'የማጽደቅ ወረፋውን መጫን አልተቻለም።',
    en: 'Could not load the approval queue.',
  },
  'approvalQueue.open': { am: 'ክፈት', en: 'Open' },

  // --- Admin all requests (pages/AllRequestsPage) -------------------------
  'allRequests.title': { am: 'ሁሉም ጥያቄዎች', en: 'All requests' },
  'allRequests.subtitle': {
    am: 'ሁሉም የገቡ ጥያቄዎች፣ ሁኔታቸው፣ ተመዳቢያቸው እና ዕድሜያቸው ጋር።',
    en: 'Every submitted request, with status, assignee, and age.',
  },
  'allRequests.filter.label': { am: 'በሁኔታ አጣራ', en: 'Filter by status' },
  'allRequests.filter.all': { am: 'ሁሉም', en: 'All' },
  'allRequests.col.ref': { am: 'የጥያቄ ቁጥር', en: 'Ref' },
  'allRequests.col.organization': { am: 'ድርጅት', en: 'Organization' },
  'allRequests.col.status': { am: 'ሁኔታ', en: 'Status' },
  'allRequests.col.assignee': { am: 'ተመዳቢ', en: 'Assignee' },
  'allRequests.col.age': { am: 'ዕድሜ', en: 'Age' },
  'allRequests.unassigned': { am: 'ያልተመደበ', en: 'Unassigned' },
  'allRequests.assignLabel': { am: 'ሽያጭ ሠራተኛ መድብ', en: 'Assign sales user' },
  'allRequests.assigning': { am: 'በመመደብ ላይ...', en: 'Assigning...' },
  'allRequests.emptyTitle': { am: 'ገና ምንም ጥያቄ የለም', en: 'No requests yet' },
  'allRequests.emptyBody': {
    am: 'ገዥዎች ጥያቄ ሲያስገቡ እዚህ ይታያሉ።',
    en: 'Requests will show up here as buyers submit them.',
  },
  'allRequests.emptyFilteredTitle': {
    am: 'ለዚህ ማጣሪያ ምንም ጥያቄ የለም',
    en: 'No requests match this filter',
  },
  'allRequests.emptyFilteredBody': {
    am: 'ሌላ ሁኔታ ይምረጡ ወይም "ሁሉም" የሚለውን ይምረጡ።',
    en: 'Try a different status, or choose "All".',
  },
  'allRequests.errorBody': { am: 'ጥያቄዎችን መጫን አልተቻለም።', en: 'Could not load requests.' },
  'allRequests.openDetail': { am: 'የጥያቄውን ዝርዝር ክፈት', en: 'Open request detail' },
  'allRequests.turnaround.summary': {
    am: 'ባለፉት 90 ቀናት አማካይ የፕሮፎርማ ማዘጋጃ ጊዜ፦ {hours} ሰዓት ({count} ፕሮፎርማዎች)',
    en: 'Average time to proforma (last 90 days): {hours}h across {count} proformas',
  },
  'allRequests.turnaround.noData': {
    am: 'ገና በቂ መረጃ የለም — ባለፉት 90 ቀናት ምንም ፕሮፎርማ አልተላከም።',
    en: 'Not enough data yet — no proforma sent in the last 90 days.',
  },
  'allRequests.turnaround.loading': {
    am: 'የጊዜ መለኪያ በመጫን ላይ...',
    en: 'Loading turnaround metric...',
  },
  'allRequests.turnaround.error': {
    am: 'የጊዜ መለኪያውን መጫን አልተቻለም።',
    en: 'Could not load the turnaround metric.',
  },

  // --- Admin request detail (pages/AdminRequestDetailPage,
  //     route /all-requests/:requestId) — read-only -----------------------
  'requestDetail.back': { am: '← ወደ ሁሉም ጥያቄዎች', en: '← Back to all requests' },
  'requestDetail.readOnlyHint': {
    am: 'ለንባብ ብቻ። ይህን ጥያቄ ከዝርዝሩ ላይ መድብ።',
    en: 'Read-only. Assign this request from the list.',
  },
  'requestDetail.errorBody': {
    am: 'የጥያቄውን ዝርዝር መጫን አልተቻለም።',
    en: 'Could not load the request detail.',
  },
  'requestDetail.header.status': { am: 'ሁኔታ', en: 'Status' },
  'requestDetail.header.submitted': { am: 'የቀረበበት ቀን', en: 'Submitted' },
  'requestDetail.header.neededBy': { am: 'የሚፈለግ የማድረሻ ቀን', en: 'Required delivery date' },
  'requestDetail.header.purpose': { am: 'ዓላማ', en: 'Purpose' },
  'requestDetail.header.assignee': { am: 'ተመዳቢ ሽያጭ ሠራተኛ', en: 'Assigned salesperson' },
  'requestDetail.header.submittedBy': { am: 'ያስገባው', en: 'Submitted by' },
  'requestDetail.header.buyerNote': { am: 'የገዥ ማስታወሻ', en: "Buyer's note" },
  'requestDetail.header.unassigned': { am: 'ያልተመደበ', en: 'Unassigned' },
  'requestDetail.header.none': { am: '—', en: '—' },
  'requestDetail.contact.title': { am: 'ድርጅትና የመገናኛ መረጃ', en: 'Organization & contact' },
  'requestDetail.contact.person': { am: 'የመገናኛ ሰው', en: 'Contact person' },
  'requestDetail.contact.email': { am: 'ኢሜይል', en: 'Email' },
  'requestDetail.contact.phone': { am: 'ስልክ', en: 'Phone' },
  'requestDetail.contact.address': { am: 'አድራሻ', en: 'Address' },
  'requestDetail.contact.tin': { am: 'ግብር ከፋይ መለያ (TIN)', en: 'TIN' },

  'requestDetail.lines.title': { am: 'የተጠየቁ ዕቃዎች', en: 'Line items' },
  'requestDetail.lines.subtitle': {
    am: 'ገዥው እንዳስገባው — ከ Excel ለመጡ መስመሮች የገዥው የመጀመሪያ አጻጻፍ ጭምር።',
    en: "As submitted by the buyer — including the buyer's original wording for Excel-imported lines.",
  },
  'requestDetail.lines.col.line': { am: 'ተራ', en: 'Line' },
  'requestDetail.lines.col.description': { am: 'መግለጫ', en: 'Description' },
  'requestDetail.lines.col.unit': { am: 'መለኪያ', en: 'Unit' },
  'requestDetail.lines.col.qty': { am: 'ብዛት', en: 'Qty' },
  'requestDetail.lines.col.matched': { am: 'የተዛመደ የካታሎግ ዕቃ', en: 'Matched catalog product' },
  'requestDetail.lines.noMatch': { am: 'ምንም ተዛማጅ የለም', en: 'No match' },
  'requestDetail.lines.empty': {
    am: 'ገና ምንም መስመር የለም። አንድ የ Excel ፋይል ተያይዞ ከሆነ አንድ ሽያጭ ሠራተኛ አምዶቹን ካዛመደ በኋላ መስመሮች ይፈጠራሉ።',
    en: 'No line items yet. If a file is attached, lines are created once a salesperson maps its columns.',
  },

  'requestDetail.attachments.title': { am: 'ተያይዘው የመጡ ፋይሎች', en: 'Attachments' },
  'requestDetail.attachments.empty': { am: 'ምንም ፋይል አልተያያዘም።', en: 'No files attached.' },

  'requestDetail.quotations.title': { am: 'የዋጋ ማቅረቢያ ታሪክ', en: 'Quotation history' },
  'requestDetail.quotations.none': {
    am: 'ለዚህ ጥያቄ ገና ምንም የዋጋ ማቅረቢያ አልተፈጠረም።',
    en: 'No quotation has been created for this request yet.',
  },
  'requestDetail.quotations.col.proforma': { am: 'የፕሮፎርማ ቁጥር', en: 'Proforma no.' },
  'requestDetail.quotations.col.revision': { am: 'ማሻሻያ', en: 'Revision' },
  'requestDetail.quotations.col.status': { am: 'ሁኔታ', en: 'Status' },
  'requestDetail.quotations.col.total': { am: 'ጠቅላላ ድምር', en: 'Total' },
  'requestDetail.quotations.noNumber': { am: 'ቁጥር አልተመደበም', en: 'No number yet' },
  'requestDetail.quotations.view': { am: 'ፕሮፎርማ ክፈት', en: 'View proforma' },

  'requestDetail.notes.title': { am: 'ማስታወሻዎች', en: 'Notes' },
  'requestDetail.notes.empty': { am: 'ገና ምንም ማስታወሻ የለም።', en: 'No notes yet.' },
  'requestDetail.notes.fromStaff': { am: 'ሠራተኛ', en: 'Staff' },
  'requestDetail.notes.fromBuyer': { am: 'ገዥ', en: 'Buyer' },

  'requestDetail.timeline.title': { am: 'የእንቅስቃሴ ታሪክ', en: 'Activity timeline' },
  'requestDetail.timeline.empty': { am: 'ገና ምንም እንቅስቃሴ የለም።', en: 'No activity yet.' },
  'requestDetail.timeline.by': { am: 'በ {name}', en: 'by {name}' },
  'requestDetail.timeline.assignedTo': { am: 'ለ {name}', en: 'to {name}' },
  'requestDetail.timeline.revisionTag': { am: 'ማሻሻያ {n}', en: 'Rev {n}' },
  'requestDetail.timeline.event.submitted': { am: 'ጥያቄ ቀረበ', en: 'Request submitted' },
  'requestDetail.timeline.event.assigned': { am: 'ተመደበ', en: 'Assigned' },
  'requestDetail.timeline.event.priced': { am: 'ዋጋ ተያዘ', en: 'Priced' },
  'requestDetail.timeline.event.submitted_for_approval': {
    am: 'ለማጽደቅ ቀረበ',
    en: 'Submitted for approval',
  },
  'requestDetail.timeline.event.approved': { am: 'ጸደቀ', en: 'Approved' },
  'requestDetail.timeline.event.rejected': { am: 'ተመለሰ', en: 'Rejected' },
  'requestDetail.timeline.event.sent': { am: 'ለገዥ ተላከ', en: 'Sent to buyer' },
  'requestDetail.timeline.event.revised': { am: 'አዲስ ማሻሻያ ተፈጠረ', en: 'Revision created' },

  'requestDetail.notifications.title': { am: 'የተላኩ ማሳወቂያዎች', en: 'Notifications sent' },
  'requestDetail.notifications.empty': {
    am: 'ገና ምንም ማሳወቂያ አልተላከም።',
    en: 'No notifications sent yet.',
  },
  'requestDetail.notifications.col.when': { am: 'ጊዜ', en: 'When' },
  'requestDetail.notifications.col.type': { am: 'ዓይነት', en: 'Type' },
  'requestDetail.notifications.col.channel': { am: 'መንገድ', en: 'Channel' },
  'requestDetail.notifications.col.recipient': { am: 'ተቀባይ', en: 'Recipient' },
  'requestDetail.notifications.col.status': { am: 'ውጤት', en: 'Outcome' },
  'requestDetail.notifications.channel.telegram': { am: 'ቴሌግራም', en: 'Telegram' },
  'requestDetail.notifications.channel.email': { am: 'ኢሜይል', en: 'Email' },
  'requestDetail.notifications.channel.whatsapp': { am: 'ዋትስአፕ', en: 'WhatsApp' },
  'requestDetail.notifications.channel.in_app': { am: 'በመተግበሪያ ውስጥ', en: 'In-app' },
  'requestDetail.notifications.recipient.customer': { am: 'ገዥ', en: 'Customer' },
  'requestDetail.notifications.recipient.sales': { am: 'ሽያጭ ሠራተኛ', en: 'Sales' },
  'requestDetail.notifications.recipient.admin': { am: 'አስተዳዳሪ', en: 'Admin' },
  'requestDetail.notifications.status.queued': { am: 'በተራ ላይ', en: 'Queued' },
  'requestDetail.notifications.status.sent': { am: 'ተልኳል', en: 'Sent' },
  'requestDetail.notifications.status.failed': { am: 'አልተሳካም', en: 'Failed' },
  'requestDetail.notifications.status.skipped': { am: 'ታልፏል', en: 'Skipped' },
  'requestDetail.notifications.attempts': { am: 'ሙከራዎች {n}', en: '{n} attempts' },
  'requestDetail.notifications.type.request_received': {
    am: 'ጥያቄ መቀበል ማረጋገጫ',
    en: 'Request received confirmation',
  },
  'requestDetail.notifications.type.request_note': {
    am: 'አዲስ ማስታወሻ ማሳወቂያ',
    en: 'New note notice',
  },
  'requestDetail.notifications.type.revision_ready': {
    am: 'የተሻሻለ ፕሮፎርማ ዝግጁ ነው',
    en: 'Revised quotation ready',
  },
  'requestDetail.notifications.type.quotation_ready_notice': {
    am: 'ፕሮፎርማ ዝግጁ ነው',
    en: 'Quotation ready',
  },
  'requestDetail.notifications.type.payment_request_ready': {
    am: 'የክፍያ ጥያቄ ዝግጁ ነው',
    en: 'Payment request ready',
  },
  'requestDetail.notifications.type.quotation_expiring_reminder': {
    am: 'የፕሮፎርማ ጊዜ ማብቂያ ማስታወሻ',
    en: 'Quotation expiring reminder',
  },
  'requestDetail.notifications.type.quotation_expired_notice': {
    am: 'ፕሮፎርማ ጊዜው አልፎበታል',
    en: 'Quotation expired',
  },
  'requestDetail.notifications.type.quotation_awaiting_approval_reminder': {
    am: 'ማጽደቅ የሚጠባበቅ ማስታወሻ',
    en: 'Awaiting approval reminder',
  },
  'requestDetail.notifications.type.request_unpriced_reminder': {
    am: 'ያልተተመነ ጥያቄ ማስታወሻ',
    en: 'Unpriced request reminder',
  },
  'requestDetail.notifications.type.payment_overdue_buyer_reminder': {
    am: 'የክፍያ መዘግየት ማስታወሻ (ለገዥ)',
    en: 'Payment overdue reminder (buyer)',
  },
  'requestDetail.notifications.type.payment_overdue_admin_reminder': {
    am: 'የክፍያ መዘግየት ማስታወሻ (ለአስተዳዳሪ)',
    en: 'Payment overdue reminder (admin)',
  },

  // --- All proformas (pages/AllProformasPage, route /all-proformas) -------
  'allProformas.title': { am: 'ሁሉም ፕሮፎርማዎች', en: 'All proformas' },
  'allProformas.subtitle': {
    am: 'እያንዳንዱ የዋጋ ማቅረቢያ በሁሉም ሁኔታ — ረቂቅ እስከ በአዲስ የተተካ። የተተኩ ማሻሻያዎችን ጨምሮ ሙሉ ታሪኩ።',
    en: 'Every quotation in every state — draft through superseded. The full history, superseded revisions included.',
  },
  'allProformas.searchPlaceholder': {
    am: 'በፕሮፎርማ ወይም በጥያቄ ቁጥር ፈልግ',
    en: 'Search by proforma or request number',
  },
  'allProformas.filter.status': { am: 'ሁኔታ', en: 'Status' },
  'allProformas.filter.allStatuses': { am: 'ሁሉም ሁኔታዎች', en: 'All statuses' },
  'allProformas.filter.organization': { am: 'ድርጅት', en: 'Organization' },
  'allProformas.filter.allOrganizations': { am: 'ሁሉም ድርጅቶች', en: 'All organizations' },
  'allProformas.filter.pricedBy': { am: 'ዋጋ የሰጠው', en: 'Priced by' },
  'allProformas.filter.allSalespeople': { am: 'ሁሉም ሠራተኞች', en: 'All salespeople' },
  'allProformas.filter.unpriced': { am: 'ዋጋ ያልተሰጠው', en: 'Unpriced' },
  'allProformas.filter.dateFrom': { am: 'ከቀን', en: 'From' },
  'allProformas.filter.dateTo': { am: 'እስከ ቀን', en: 'To' },
  'allProformas.col.proforma': { am: 'ፕሮፎርማ ቁጥር', en: 'Proforma no.' },
  'allProformas.col.organization': { am: 'ድርጅት', en: 'Organization' },
  'allProformas.col.pricedBy': { am: 'ዋጋ የሰጠው', en: 'Priced by' },
  'allProformas.col.status': { am: 'ሁኔታ', en: 'Status' },
  'allProformas.col.total': { am: 'ጠቅላላ ድምር', en: 'Total' },
  'allProformas.col.validUntil': { am: 'የሚያበቃበት ቀን', en: 'Valid until' },
  'allProformas.col.revision': { am: 'ማሻሻያ', en: 'Rev.' },
  'allProformas.notAllocated': { am: 'ቁጥር አልተመደበም', en: 'not allocated' },
  'allProformas.unpriced': { am: '—', en: '—' },
  'allProformas.count': { am: '{total} ፕሮፎርማዎች', en: '{total} proformas' },
  'allProformas.countFiltered': {
    am: 'ከ{total} ውስጥ {shown} ይታያሉ',
    en: 'Showing {shown} of {total}',
  },
  'allProformas.emptyTitle': { am: 'ገና ምንም ፕሮፎርማ የለም', en: 'No proformas yet' },
  'allProformas.emptyBody': {
    am: 'ሽያጭ ጥያቄዎችን ዋጋ ሲሰጥ የዋጋ ማቅረቢያዎች እዚህ ይታያሉ።',
    en: 'Quotations appear here as sales prices requests.',
  },
  'allProformas.emptyFilteredTitle': {
    am: 'ለዚህ ማጣሪያ ምንም ፕሮፎርማ የለም',
    en: 'No proformas match these filters',
  },
  'allProformas.emptyFilteredBody': {
    am: 'ማጣሪያዎቹን ያስተካክሉ ወይም ፍለጋውን ያጽዱ።',
    en: 'Adjust the filters or clear the search.',
  },
  'allProformas.errorBody': { am: 'ፕሮፎርማዎችን መጫን አልተቻለም።', en: 'Could not load proformas.' },

  // --- Read-only proforma viewer (pages/AllProformasViewPage) ------------
  'allProformas.view.back': { am: '← ወደ ሁሉም ፕሮፎርማዎች', en: '← All proformas' },
  'allProformas.view.errorBody': {
    am: 'ይህን ፕሮፎርማ መጫን አልተቻለም።',
    en: 'Could not load this proforma.',
  },
  'allProformas.view.readOnlyHint': {
    am: 'ለንባብ ብቻ። ማጽደቅና ውድቅ ማድረግ በማጽደቅ ማያ ገጽ ላይ ይቀራል።',
    en: 'Read-only. Approval and rejection stay on the approval screen.',
  },
  'allProformas.view.supersededNote': {
    am: 'ይህ ፕሮፎርማ በአዲስ ማሻሻያ ተተክቷል፡',
    en: 'This proforma was superseded by',
  },
  'allProformas.view.theRevision': { am: 'ማሻሻያ {n}', en: 'revision {n}' },
  'allProformas.view.declinedNote': {
    am: 'ገዥው ይህን ፕሮፎርማ ውድቅ አድርጓል። ከዚህ በታች እንደተላከው ተመዝግቧል።',
    en: 'The buyer declined this proforma. It is shown below as it was sent.',
  },
  'allProformas.view.expiredNote': {
    am: 'ይህ ፕሮፎርማ ጊዜው አልፎበታል። ከዚህ በታች እንደተላከው ተመዝግቧል።',
    en: 'This proforma has expired. It is shown below as it was sent.',
  },
  'allProformas.view.rejectedNote': {
    am: 'ይህ የዋጋ ማቅረቢያ በአስተዳዳሪ ውድቅ ተደርጎ ወደ ዋጋ አሰጣጥ ተመልሷል። የፕሮፎርማ ቁጥር አልተመደበም።',
    en: 'This quotation was rejected by an admin and returned to pricing. No proforma number was allocated.',
  },
  'allProformas.view.sealedNote': {
    am: 'ይህ ማቅረቢያ የታሸገ ጨረታ ነው — ገዥው አሁን የማጣቀሻ ቁጥር እና ሁኔታ ብቻ ያያል፣ ሙሉ ይዘቱን አያይም።',
    en: 'This proforma is a sealed tender — the buyer currently sees only a reference number and status, not the full content.',
  },
  'allProformas.view.requote.title': { am: 'ዳግም ዋጋ መስጠት', en: 'Re-quote' },
  'allProformas.view.requote.hint': {
    am: 'ይህ ፕሮፎርማ ጊዜው አልፎበታል። በአሁኑ ዋጋዎች አዲስ ማሻሻያ ይፍጠሩ።',
    en: 'This proforma has expired. Generate a new revision at current prices.',
  },
  'allProformas.view.requote.button': { am: 'አሁን ዳግም ዋጋ ስጥ', en: 'Re-quote now' },
  'allProformas.view.requote.requoting': { am: 'ዳግም በመስጠት ላይ...', en: 'Re-quoting...' },
  'allProformas.view.requote.confirmTitle': {
    am: 'ይህ ፕሮፎርማ ዳግም ይሰጥ?',
    en: 'Re-quote this proforma?',
  },
  'allProformas.view.requote.confirmBody': {
    am: 'ይህ በአሁኑ ዋጋዎች አዲስ ክለሳ ይፈጥራል እና ይህኛውን እንደ ተተካ ያመለክታል።',
    en: 'This creates a new revision at current prices and marks this one as superseded.',
  },
  'allProformas.view.requote.successToast': {
    am: 'ዳግም ዋጋ ተሰጥቷል።',
    en: 'Re-quoted.',
  },

  // --- Admin catalog (pages/CatalogPage, route /products) -----------------
  'catalog.title': { am: 'ካታሎግ', en: 'Catalog' },
  'catalog.subtitle': {
    am: 'ምርቶች እና ለእያንዳንዱ የደንበኛ እርከን የተያዙ ዋጋዎች።',
    en: 'Products and the list/floor prices on file for each customer tier.',
  },
  'catalog.filter.categoryLabel': { am: 'በምድብ አጣራ', en: 'Filter by category' },
  'catalog.filter.categoryAll': { am: 'ሁሉም ምድቦች', en: 'All categories' },
  'catalog.filter.searchPlaceholder': {
    am: 'በስም፣ ብራንድ ወይም ኮድ ፈልግ...',
    en: 'Search by name, brand, or SKU...',
  },
  'catalog.newProduct': { am: 'አዲስ ምርት', en: 'New product' },

  'catalog.col.sku': { am: 'ኮድ', en: 'SKU' },
  'catalog.col.name': { am: 'ስም', en: 'Name' },
  'catalog.col.category': { am: 'ምድብ', en: 'Category' },
  'catalog.col.condition': { am: 'ሁኔታ', en: 'Condition' },
  'catalog.col.prices': { am: 'ዋጋዎች', en: 'Prices' },
  'catalog.col.actions': { am: 'ተግባር', en: 'Actions' },
  'catalog.categoryNone': { am: 'ያለ ምድብ', en: 'Uncategorized' },

  'catalog.condition.new': { am: 'አዲስ', en: 'New' },
  'catalog.condition.refurbishedManufacturer': {
    am: 'በአምራች የታደሰ',
    en: 'Refurbished (Manufacturer)',
  },
  'catalog.condition.refurbishedInhouse': { am: 'በእኛ የታደሰ', en: 'Refurbished (In-house)' },
  'catalog.condition.used': { am: 'ያገለገለ', en: 'Used' },

  'catalog.tiers.pricedCount': {
    am: '{priced}/{total} እርከኖች ዋጋ ተይዞላቸዋል',
    en: '{priced}/{total} tiers priced',
  },
  'catalog.tiers.toggleShow': { am: 'ዋጋ አርትዕ', en: 'Edit prices' },
  'catalog.tiers.toggleHide': { am: 'ደብቅ', en: 'Hide' },
  'catalog.tiers.notSet': { am: 'ዋጋ አልተያዘም', en: 'Not set' },
  'catalog.tiers.listLabel': { am: 'ዝርዝር ዋጋ', en: 'List price' },
  'catalog.tiers.floorLabel': { am: 'ዝቅተኛ ዋጋ', en: 'Floor price' },
  'catalog.tiers.save': { am: 'አስቀምጥ', en: 'Save' },
  'catalog.tiers.saving': { am: 'በማስቀመጥ ላይ...', en: 'Saving...' },
  'catalog.tiers.saved': { am: 'ተቀምጧል', en: 'Saved' },
  'catalog.tiers.floorExceedsListClient': {
    am: 'ዝቅተኛ ዋጋ ከዝርዝር ዋጋ መብለጥ አይችልም።',
    en: 'Floor price cannot exceed list price.',
  },
  'catalog.tiers.bothRequired': {
    am: 'ዝርዝር እና ዝቅተኛ ዋጋ ሁለቱም ያስፈልጋሉ።',
    en: 'Both list price and floor price are required.',
  },

  'catalog.actions.edit': { am: 'አርትዕ', en: 'Edit' },
  'catalog.actions.deactivate': { am: 'አቦዝን', en: 'Deactivate' },
  'catalog.actions.deactivating': { am: 'በማቦዘን ላይ...', en: 'Deactivating...' },
  'catalog.actions.deactivateConfirmPrompt': {
    am: 'ይህን ምርት ማቦዘን ይፈልጋሉ? ከዚህ በኋላ ለትዕዛዝ አይታይም።',
    en: 'Deactivate this product? It will no longer be orderable.',
  },
  'catalog.actions.deactivateConfirm': { am: 'አቦዝን', en: 'Confirm deactivate' },
  'catalog.actions.deactivateCancel': { am: 'ይቅር', en: 'Cancel' },

  'catalog.actions.duplicate': { am: 'ቅዳ', en: 'Duplicate' },
  'catalog.actions.duplicateConfirm': { am: 'ቅዳ', en: 'Confirm duplicate' },
  'catalog.actions.duplicating': { am: 'በመቅዳት ላይ...', en: 'Duplicating...' },

  'catalog.actions.feature': { am: 'አድምቅ', en: 'Feature' },
  'catalog.actions.unfeature': { am: 'አታድምቅ', en: 'Unfeature' },
  'catalog.actions.featuredOrderLabel': { am: 'የተለየ ቅደም ተከተል', en: 'Featured order' },

  'catalog.emptyTitle': { am: 'ገና ምንም ምርት የለም', en: 'No products yet' },
  'catalog.emptyBody': {
    am: 'ካታሎግ ባዶ ነው። "አዲስ ምርት" ብለው የመጀመሪያውን ምርት ያክሉ።',
    en: 'The catalog is empty. Add the first product with "New product".',
  },
  'catalog.emptyFilteredTitle': {
    am: 'ለዚህ ማጣሪያ ምንም ምርት አልተገኘም',
    en: 'No products match this filter',
  },
  'catalog.emptyFilteredBody': {
    am: 'ሌላ ምድብ ወይም የፍለጋ ቃል ይሞክሩ።',
    en: 'Try a different category or search term.',
  },
  'catalog.errorBody': { am: 'ካታሎግ መጫን አልተቻለም።', en: 'Could not load the catalog.' },

  'catalog.form.createTitle': { am: 'አዲስ ምርት ጨምር', en: 'Add a new product' },
  'catalog.form.editTitle': { am: 'ምርት አርትዕ', en: 'Edit product' },
  'catalog.form.skuLabel': { am: 'ኮድ (SKU)', en: 'SKU' },
  'catalog.form.skuImmutableHint': {
    am: 'ኮድ (SKU) ከተፈጠረ በኋላ ሊቀየር አይችልም።',
    en: 'The SKU cannot be changed once a product is created.',
  },
  'catalog.form.nameLabel': { am: 'ስም (እንግሊዝኛ)', en: 'Name (English)' },
  'catalog.form.nameAmLabel': { am: 'ስም (አማርኛ)', en: 'Name (Amharic)' },
  'catalog.form.brandLabel': { am: 'ብራንድ', en: 'Brand' },
  'catalog.form.brandNoneOption': { am: 'ያለ ብራንድ', en: 'No brand' },
  'catalog.form.brandAddNewOption': { am: '+ አዲስ ብራንድ ጨምር…', en: '+ Add new brand…' },
  'catalog.form.brandNewLabel': { am: 'አዲስ ብራንድ ስም', en: 'New brand name' },
  'catalog.form.brandAdd': { am: 'ጨምር', en: 'Add' },
  'catalog.form.brandAdding': { am: 'በመጨመር ላይ...', en: 'Adding...' },
  'catalog.form.brandNewRequired': { am: 'ብራንድ ስም ያስፈልጋል።', en: 'Brand name is required.' },
  'catalog.form.modelLabel': { am: 'ሞዴል', en: 'Model' },
  'catalog.form.specLabel': { am: 'ዝርዝር መግለጫ', en: 'Spec' },
  'catalog.form.unitLabel': { am: 'መለኪያ', en: 'Unit' },
  'catalog.form.categoryLabel': { am: 'ምድብ', en: 'Category' },
  'catalog.form.categoryNoneOption': { am: 'ያለ ምድብ', en: 'No category' },
  'catalog.form.stockQtyLabel': { am: 'የመጋዘን ብዛት (አማራጭ)', en: 'Stock quantity (optional)' },
  'catalog.form.submitCreate': { am: 'ምርት ፍጠር', en: 'Create product' },
  'catalog.form.submitting': { am: 'በመፍጠር ላይ...', en: 'Creating...' },
  'catalog.form.submitSave': { am: 'ለውጦችን አስቀምጥ', en: 'Save changes' },
  'catalog.form.saving': { am: 'በማስቀመጥ ላይ...', en: 'Saving...' },
  'catalog.form.cancel': { am: 'ይቅር', en: 'Cancel' },
  'catalog.form.nameRequired': { am: 'ስም ያስፈልጋል።', en: 'Name is required.' },
  'catalog.form.skuRequired': { am: 'ኮድ (SKU) ያስፈልጋል።', en: 'SKU is required.' },
  'catalog.form.conditionLabel': { am: 'ሁኔታ', en: 'Condition' },

  'catalog.form.attributesSectionTitle': { am: 'ውቅር', en: 'Configuration' },
  'catalog.form.attributeUnsetOption': { am: 'አልተመረጠም', en: 'Not set' },

  'catalog.form.displayDescriptionLabel': {
    am: 'የማሳያ መግለጫ (አማራጭ)',
    en: 'Display description (optional)',
  },
  'catalog.form.displayDescriptionHint': {
    am: 'ባዶ ይተዉት፤ ከላይ ካሉት መስኮች ራስ-ሰር ይፈጠራል።',
    en: 'Leave blank to generate automatically from the fields above.',
  },

  'catalog.form.photoSectionTitle': { am: 'የምርት ፎቶ', en: 'Product photo' },
  'catalog.form.photoAltText': { am: 'የምርት ፎቶ', en: 'Product photo' },
  'catalog.form.photoNone': { am: 'ምንም ፎቶ የለም', en: 'No photo' },
  'catalog.form.photoChoose': { am: 'ፋይል ምረጥ', en: 'Choose file' },
  'catalog.form.photoUpload': { am: 'ፎቶ ስቀል', en: 'Upload photo' },
  'catalog.form.photoUploading': { am: 'በመስቀል ላይ...', en: 'Uploading...' },
  'catalog.form.photoHint': {
    am: 'JPEG፣ PNG ወይም WebP፣ እስከ 8ሜባ ድረስ።',
    en: 'JPEG, PNG, or WebP, up to 8MB.',
  },
  'catalog.form.photoInvalidType': {
    am: 'JPEG፣ PNG ወይም WebP ፋይል ብቻ ተቀባይነት አለው።',
    en: 'Only JPEG, PNG, or WebP files are accepted.',
  },
  'catalog.form.photoTooLarge': {
    am: 'ፎቶው ከ8ሜባ መብለጥ አይችልም።',
    en: 'The photo cannot exceed 8MB.',
  },

  // --- Admin cost management (pages/CostManagementPage, admin-only) -------
  'costManagement.title': { am: 'የግዢ ዋጋ አያያዝ', en: 'Cost management' },
  'costManagement.subtitle': {
    am: 'የእያንዳንዱ ምርት የግዢ ዋጋና አቅራቢ። ይህ ገጽ ለአስተዳዳሪ ብቻ የሚታይ ነው።',
    en: 'Cost price and supplier per product. Visible to admin only.',
  },
  'costManagement.filter.searchLabel': { am: 'ምርት ፈልግ', en: 'Search products' },
  'costManagement.filter.searchPlaceholder': {
    am: 'በስም ፈልግ...',
    en: 'Search by name...',
  },
  'costManagement.filter.staleOnlyLabel': {
    am: 'ጊዜው ያለፈባቸውን ብቻ አሳይ',
    en: 'Show stale only',
  },

  'costManagement.col.sku': { am: 'ኮድ', en: 'SKU' },
  'costManagement.col.name': { am: 'ስም', en: 'Name' },
  'costManagement.col.costPrice': { am: 'የግዢ ዋጋ', en: 'Cost price' },
  'costManagement.col.supplier': { am: 'አቅራቢ', en: 'Supplier' },
  'costManagement.col.updated': { am: 'የተሻሻለበት ቀን', en: 'Updated' },
  'costManagement.col.actions': { am: 'ተግባር', en: 'Actions' },

  'costManagement.notSet': { am: 'ገና አልተያዘም', en: 'Not set' },
  'costManagement.supplierNotSet': { am: 'አቅራቢ አልተመዘገበም', en: 'No supplier on file' },
  'costManagement.neverUpdated': { am: 'በጭራሽ አልተሻሻለም', en: 'Never updated' },

  'costManagement.stale.badge': { am: 'ጊዜው ያለፈበት', en: 'Stale' },

  'costManagement.actions.edit': { am: 'አርትዕ', en: 'Edit' },
  'costManagement.actions.setCost': { am: 'ዋጋ ይያዙ', en: 'Set cost' },

  'costManagement.form.costPriceLabel': { am: 'የግዢ ዋጋ', en: 'Cost price' },
  'costManagement.form.supplierLabel': { am: 'አቅራቢ (አማራጭ)', en: 'Supplier (optional)' },
  'costManagement.form.save': { am: 'አስቀምጥ', en: 'Save' },
  'costManagement.form.saving': { am: 'በማስቀመጥ ላይ...', en: 'Saving...' },
  'costManagement.form.cancel': { am: 'ይቅር', en: 'Cancel' },
  'costManagement.form.costPriceRequired': {
    am: 'የግዢ ዋጋ ያስፈልጋል።',
    en: 'Cost price is required.',
  },
  'costManagement.form.costPriceInvalid': {
    am: 'ትክክለኛ አዎንታዊ ዋጋ ያስገቡ (ቢበዛ 2 የአስርዮሽ ቁጥሮች)።',
    en: 'Enter a valid positive amount (up to 2 decimal places).',
  },

  'costManagement.emptyTitle': { am: 'ገና ምንም ምርት የለም', en: 'No products yet' },
  'costManagement.emptyBody': {
    am: 'የግዢ ዋጋ ከማስቀመጥዎ በፊት መጀመሪያ በካታሎግ ውስጥ ምርት ይፍጠሩ።',
    en: 'Add a product in the catalog before setting a cost price for it.',
  },
  'costManagement.emptyFilteredTitle': {
    am: 'ምንም ተዛማጅ ምርት አልተገኘም',
    en: 'No matching products',
  },
  'costManagement.emptyFilteredBody': {
    am: 'ማጣሪያዎን ይቀይሩ ወይም ያፅዱ።',
    en: 'Try changing or clearing your filter.',
  },
  'costManagement.errorBody': {
    am: 'የግዢ ዋጋ ውሂብ መጫን አልተቻለም።',
    en: 'Could not load cost data.',
  },

  // --- Admin users (pages/UsersPage, admin-only) --------------------------
  'users.title': { am: 'ተጠቃሚዎች', en: 'Users' },
  'users.subtitle': {
    am: 'የሽያጭ ሠራተኞች መለያዎች — ይፍጠሩ እና ሲያስፈልግ ያቦዝኑ።',
    en: 'Sales staff accounts — create them, and deactivate when needed.',
  },
  'users.newUser': { am: 'አዲስ ተጠቃሚ', en: 'New user' },

  'users.col.name': { am: 'ሙሉ ስም', en: 'Full name' },
  'users.col.email': { am: 'ኢሜይል', en: 'Email' },
  'users.col.phone': { am: 'ስልክ', en: 'Phone' },
  'users.col.status': { am: 'ሁኔታ', en: 'Status' },
  'users.col.lastLogin': { am: 'መጨረሻ የገባበት ጊዜ', en: 'Last login' },
  'users.col.permissions': { am: 'ፈቃዶች', en: 'Permissions' },
  'users.col.actions': { am: 'ተግባር', en: 'Actions' },

  // Per-user permissions (migration 016). Row-presence grant model — a
  // sales user either holds a permission or does not. Currently one:
  // can_approve_revisions (approve a quantity-only, floor-clean buyer-review
  // revision without an admin). Keys MUST match userService.GRANTABLE_PERMISSIONS.
  'users.permission.can_approve_revisions.label': {
    am: 'ማሻሻያዎችን ማጽደቅ',
    en: 'Approve revisions',
  },
  'users.permission.can_approve_revisions.hint': {
    am: 'ብዛት ብቻ የተቀየረ፣ ከወለል በታች ችግር የሌለው የገዥ-ግምገማ ማሻሻያን ያለ አስተዳዳሪ ማጽደቅ ይችላል።',
    en: 'Can approve a quantity-only, floor-clean buyer-review revision without an admin.',
  },
  // Sealed-bid tenders (migration 020) / re-quote / assignment — three more
  // grantable permissions (utils/permissionCatalog.js). Keys MUST still
  // match that catalogue exactly; PermissionToggles now renders whatever it
  // returns, not a hardcoded local list.
  'users.permission.can_unseal.label': { am: 'የታሸጉ ጨረታዎችን መክፈት', en: 'Unseal sealed tenders' },
  'users.permission.can_unseal.hint': {
    am: 'የታቀደው የመክፈቻ ጊዜ ከመድረሱ በፊት ወይም በዚያኑ ጊዜ የታሸገ ጨረታን ይዘት መልቀቅ ይችላል። ይህ ፈቃድ ከሌለ ጥያቄው ወደ አስተዳዳሪ ይተላለፋል።',
    en: "Can release a sealed tender's contents on demand. Without it, the request routes to an admin.",
  },
  'users.permission.can_requote.label': {
    am: 'ያለቁ ማቅረቢያዎችን ዳግም ማቅረብ',
    en: 'Re-quote expired proformas',
  },
  'users.permission.can_requote.hint': {
    am: 'ያለቀ ማቅረቢያን ያለ አስተዳዳሪ በአሁኑ ዋጋዎች ዳግም መፍጠር ይችላል።',
    en: 'Can regenerate an expired quotation at current prices without an admin.',
  },
  'users.permission.can_assign.label': { am: 'ጥያቄዎችን መመደብ', en: 'Assign requests' },
  'users.permission.can_assign.hint': {
    am: 'ጥያቄዎችን ለሌሎች የሽያጭ ሰራተኞች መመደብ እና እንደገና መመደብ ይችላል።',
    en: 'Can assign and reassign requests to other salespeople.',
  },
  'users.permission.grantedToast': { am: 'ፈቃድ ተሰጥቷል', en: 'Permission granted' },
  'users.permission.revokedToast': { am: 'ፈቃድ ተነስቷል', en: 'Permission revoked' },
  'users.permission.updating': { am: 'በማዘመን ላይ...', en: 'Updating...' },

  'users.status.active': { am: 'ንቁ', en: 'Active' },
  'users.status.inactive': { am: 'ስራ ላይ ያልሆነ', en: 'Inactive' },
  'users.phoneNotSet': { am: 'ስልክ አልተመዘገበም', en: 'No phone on file' },
  'users.neverLoggedIn': { am: 'ገና አልገባም/ችም', en: 'Never logged in' },

  'users.actions.deactivate': { am: 'አቦዝን', en: 'Deactivate' },
  'users.actions.deactivating': { am: 'በማቦዘን ላይ...', en: 'Deactivating...' },
  'users.actions.deactivateConfirmPrompt': {
    am: 'ይህ መለያ እንዲቦዝን ማድረግ ይፈልጋሉ? ወደ ኋላ መመለስ አይቻልም።',
    en: 'Deactivate this account? This cannot be undone from here.',
  },
  'users.actions.deactivateConfirm': { am: 'አቦዝን', en: 'Confirm deactivate' },
  'users.actions.deactivateCancel': { am: 'ይቅር', en: 'Cancel' },

  'users.form.fullNameLabel': { am: 'ሙሉ ስም', en: 'Full name' },
  'users.form.emailLabel': { am: 'ኢሜይል', en: 'Email' },
  'users.form.phoneLabel': { am: 'ስልክ (አማራጭ)', en: 'Phone (optional)' },
  'users.form.passwordLabel': { am: 'የይለፍ ቃል', en: 'Password' },
  'users.form.passwordHint': {
    am: 'ቢያንስ 8 ቁምፊዎች።',
    en: 'At least 8 characters.',
  },
  'users.form.submitCreate': { am: 'ተጠቃሚ ፍጠር', en: 'Create user' },
  'users.form.submitting': { am: 'በመፍጠር ላይ...', en: 'Creating...' },
  'users.form.cancel': { am: 'ይቅር', en: 'Cancel' },
  'users.form.fullNameRequired': { am: 'ሙሉ ስም ያስፈልጋል።', en: 'Full name is required.' },
  'users.form.emailRequired': { am: 'ትክክለኛ ኢሜይል ያስገቡ።', en: 'Enter a valid email.' },
  'users.form.passwordTooShort': {
    am: 'የይለፍ ቃል ቢያንስ 8 ቁምፊዎች ሊኖረው ይገባል።',
    en: 'Password must be at least 8 characters.',
  },

  'users.emptyTitle': { am: 'ገና ምንም የሽያጭ ተጠቃሚ የለም', en: 'No sales users yet' },
  'users.emptyBody': {
    am: 'የመጀመሪያውን የሽያጭ ተጠቃሚ ለመፍጠር "አዲስ ተጠቃሚ" የሚለውን ይጫኑ።',
    en: 'Create the first sales account with "New user" above.',
  },
  'users.errorBody': { am: 'ተጠቃሚዎችን መጫን አልተቻለም።', en: 'Could not load users.' },

  // --- Admin "Bank accounts" (pages/BankAccountsPage, admin-only) — the
  // accounts printed on the proforma / payment-request banking block.
  'bankAccounts.title': { am: 'የባንክ ሒሳቦች', en: 'Bank accounts' },
  'bankAccounts.subtitle': {
    am: 'በፕሮፎርማና በክፍያ ጥያቄ ሰነድ ላይ የሚታተሙ የባንክ ሒሳቦች። "በፕሮፎርማ ላይ" የተባሉት ብቻ ይታተማሉ፤ ቅደም ተከተሉ በ"ቅደም ተከተል" ይወሰናል።',
    en: 'The bank accounts printed on the proforma and the payment-request document. Only accounts marked "On proforma" are printed; "Order" sets the order they print in.',
  },
  'bankAccounts.newAccount': { am: 'አዲስ ሒሳብ', en: 'New bank account' },
  'bankAccounts.notSet': { am: '—', en: '—' },
  'bankAccounts.col.order': { am: 'ቅደም ተከተል', en: 'Order' },
  'bankAccounts.col.bankName': { am: 'የባንክ ስም', en: 'Bank name' },
  'bankAccounts.col.accountName': { am: 'የሒሳብ ስም', en: 'Account name' },
  'bankAccounts.col.accountNumber': { am: 'የሒሳብ ቁጥር', en: 'Account number' },
  'bankAccounts.col.branch': { am: 'ቅርንጫፍ', en: 'Branch' },
  'bankAccounts.col.swift': { am: 'SWIFT/BIC', en: 'SWIFT/BIC' },
  'bankAccounts.col.status': { am: 'ሁኔታ', en: 'Status' },
  'bankAccounts.col.actions': { am: 'ድርጊቶች', en: 'Actions' },
  'bankAccounts.status.onProforma': { am: 'በፕሮፎርማ ላይ', en: 'On proforma' },
  'bankAccounts.status.hidden': { am: 'ተደብቋል', en: 'Hidden' },
  'bankAccounts.actions.edit': { am: 'አስተካክል', en: 'Edit' },
  'bankAccounts.actions.deactivate': { am: 'ከፕሮፎርማ አውጣ', en: 'Remove from proforma' },
  'bankAccounts.actions.reactivate': { am: 'ወደ ፕሮፎርማ መልስ', en: 'Add back to proforma' },
  'bankAccounts.actions.deactivateConfirmTitle': {
    am: '{name}ን ከፕሮፎርማ ላውጣ?',
    en: 'Remove {name} from the proforma?',
  },
  'bankAccounts.actions.deactivateConfirmBody': {
    am: 'ይህ ሒሳብ ከአሁን በኋላ በአዳዲስ ፕሮፎርማዎችና የክፍያ ጥያቄ ሰነዶች ላይ አይታተምም። በኋላ መልሰው ማከል ይችላሉ።',
    en: 'This account will no longer print on new proformas or payment-request documents. You can add it back later.',
  },
  'bankAccounts.actions.deactivateConfirm': { am: 'አውጣ', en: 'Remove' },
  'bankAccounts.createdToast': { am: 'የባንክ ሒሳብ ተፈጥሯል', en: 'Bank account created' },
  'bankAccounts.updatedToast': { am: 'የባንክ ሒሳብ ተስተካክሏል', en: 'Bank account updated' },
  'bankAccounts.deactivatedToast': { am: 'ሒሳቡ ከፕሮፎርማ ወጥቷል', en: 'Removed from the proforma' },
  'bankAccounts.reactivatedToast': { am: 'ሒሳቡ ወደ ፕሮፎርማ ተመልሷል', en: 'Added back to the proforma' },
  'bankAccounts.emptyTitle': { am: 'ገና ምንም የባንክ ሒሳብ የለም', en: 'No bank accounts yet' },
  'bankAccounts.emptyBody': {
    am: 'የመጀመሪያውን የባንክ ሒሳብ ለመጨመር ከላይ "አዲስ ሒሳብ" የሚለውን ይጫኑ። ያለ ቢያንስ አንድ ሒሳብ ፕሮፎርማው የባንክ ዝርዝር ማተም አይችልም።',
    en: 'Add the first bank account with "New bank account" above. The proforma has nothing to print in its banking block without at least one.',
  },
  'bankAccounts.errorBody': {
    am: 'የባንክ ሒሳቦችን መጫን አልተቻለም።',
    en: 'Could not load bank accounts.',
  },
  'bankAccounts.form.createTitle': { am: 'አዲስ የባንክ ሒሳብ', en: 'New bank account' },
  'bankAccounts.form.editTitle': { am: 'የባንክ ሒሳብ አስተካክል', en: 'Edit bank account' },
  'bankAccounts.form.bankNameLabel': { am: 'የባንክ ስም', en: 'Bank name' },
  'bankAccounts.form.accountNameLabel': { am: 'የሒሳብ ስም', en: 'Account name' },
  'bankAccounts.form.accountNumberLabel': { am: 'የሒሳብ ቁጥር', en: 'Account number' },
  'bankAccounts.form.branchLabel': { am: 'ቅርንጫፍ (አማራጭ)', en: 'Branch (optional)' },
  'bankAccounts.form.swiftLabel': { am: 'SWIFT/BIC (አማራጭ)', en: 'SWIFT/BIC (optional)' },
  'bankAccounts.form.sortOrderLabel': { am: 'ቅደም ተከተል', en: 'Order' },
  'bankAccounts.form.sortOrderHint': {
    am: 'ዝቅተኛ ቁጥር በፕሮፎርማው ላይ ቀድሞ ይታተማል።',
    en: 'A lower number prints first on the proforma.',
  },
  'bankAccounts.form.bankNameRequired': { am: 'የባንክ ስም ያስፈልጋል።', en: 'Bank name is required.' },
  'bankAccounts.form.accountNameRequired': {
    am: 'የሒሳብ ስም ያስፈልጋል።',
    en: 'Account name is required.',
  },
  'bankAccounts.form.accountNumberRequired': {
    am: 'የሒሳብ ቁጥር ያስፈልጋል።',
    en: 'Account number is required.',
  },
  'bankAccounts.form.cancel': { am: 'ይቅር', en: 'Cancel' },
  'bankAccounts.form.submitCreate': { am: 'ሒሳብ ፍጠር', en: 'Create account' },
  'bankAccounts.form.submitEdit': { am: 'ለውጦችን አስቀምጥ', en: 'Save changes' },
  'bankAccounts.form.submitting': { am: 'በማስቀመጥ ላይ...', en: 'Saving...' },

  // --- Admin "Categories & attributes" (pages/CategoriesPage, admin-only) —
  // the catalog tree and each category's product-attribute schema.
  'categories.title': { am: 'ምድቦችና ባህሪያት', en: 'Categories & attributes' },
  'categories.subtitle': {
    am: 'የካታሎግ ዛፍና የእያንዳንዱ ምድብ የምርት ባህሪያት። አንድ ምድብ ይምረጡ ለማስተካከል።',
    en: "The catalog tree and each category's product attributes. Select a category to edit it.",
  },
  'categories.newCategory': { am: 'አዲስ ምድብ', en: 'New category' },
  'categories.selectPrompt': {
    am: 'ለማስተካከል ከግራ በኩል አንድ ምድብ ይምረጡ።',
    en: 'Select a category on the left to edit it.',
  },
  'categories.inactiveTag': { am: 'ታግዷል', en: 'Inactive' },
  'categories.detailInactiveNote': {
    am: 'ይህ ምድብ ታግዷል — በገዥ በኩል አይታይም።',
    en: 'This category is inactive — it is hidden from buyers.',
  },
  'categories.editDetails': { am: 'ዝርዝሮችን አስተካክል', en: 'Edit details' },
  'categories.deactivate': { am: 'አግድ', en: 'Deactivate' },
  'categories.delete': { am: 'ሰርዝ', en: 'Delete' },
  'categories.updatedToast': { am: 'ምድቡ ተስተካክሏል', en: 'Category updated' },
  'categories.createdToast': { am: 'ምድቡ ተፈጥሯል', en: 'Category created' },
  'categories.deactivatedToast': { am: 'ምድቡ ታግዷል', en: 'Category deactivated' },
  'categories.deletedToast': { am: 'ምድቡ ተሰርዟል', en: 'Category deleted' },
  'categories.deactivateConfirmTitle': {
    am: '{name}ን ላግድ?',
    en: 'Deactivate {name}?',
  },
  'categories.deactivateConfirmBody': {
    am: 'ይህ ምድብ ከገዥ ካታሎግ ይደበቃል። በውስጡ ያሉ ምርቶች ግን አይነኩም። በኋላ መልሶ ማስጀመር ይቻላል።',
    en: 'This category will be hidden from the buyer catalog. Products in it are untouched. It can be reactivated later.',
  },
  'categories.deleteConfirmTitle': {
    am: '{name}ን ላስወግድ?',
    en: 'Permanently delete {name}?',
  },
  'categories.deleteConfirmBody': {
    am: 'ይህ በቋሚነት ያስወግደዋል። ምርቶች ወይም ንዑስ ምድቦች ካሉት አይሰራም — በምትኩ አግድ።',
    en: 'This removes it for good. It only works if the category has no products and no subcategories — otherwise deactivate it instead.',
  },
  'categories.emptyTitle': { am: 'ገና ምንም ምድብ የለም', en: 'No categories yet' },
  'categories.emptyBody': {
    am: 'የመጀመሪያውን ምድብ ለመፍጠር "አዲስ ምድብ" የሚለውን ይጫኑ።',
    en: 'Create the first category with "New category" above.',
  },
  'categories.errorBody': { am: 'ምድቦችን መጫን አልተቻለም።', en: 'Could not load categories.' },
  'categories.form.createTitle': { am: 'አዲስ ምድብ', en: 'New category' },
  'categories.form.editTitle': { am: 'ምድብ አስተካክል', en: 'Edit category' },
  'categories.form.nameLabel': { am: 'ስም (እንግሊዝኛ)', en: 'Name (English)' },
  'categories.form.nameAmLabel': { am: 'ስም (አማርኛ)', en: 'Name (Amharic)' },
  'categories.form.parentLabel': { am: 'ወላጅ ምድብ', en: 'Parent category' },
  'categories.form.parentNone': { am: '— ከፍተኛ ደረጃ —', en: '— Top level —' },
  'categories.form.sortOrderLabel': { am: 'ቅደም ተከተል', en: 'Order' },
  'categories.form.sortOrderHint': {
    am: 'ዝቅተኛ ቁጥር ቀድሞ ይታያል።',
    en: 'A lower number shows first.',
  },
  'categories.form.nameRequired': { am: 'የእንግሊዝኛ ስም ያስፈልጋል።', en: 'An English name is required.' },
  'categories.form.cancel': { am: 'ይቅር', en: 'Cancel' },
  'categories.form.submitCreate': { am: 'ምድብ ፍጠር', en: 'Create category' },
  'categories.form.submitEdit': { am: 'ለውጦችን አስቀምጥ', en: 'Save changes' },
  'categories.form.submitting': { am: 'በማስቀመጥ ላይ...', en: 'Saving...' },

  // Attributes (the schema, per category)
  'categories.attr.title': { am: 'የምርት ባህሪያት', en: 'Product attributes' },
  'categories.attr.newAttribute': { am: 'አዲስ ባህሪ', en: 'New attribute' },
  'categories.attr.empty': {
    am: 'ይህ ምድብ የራሱ ባህሪ የለውም። ከወላጅ ምድቦች ባህሪያትን ይወርሳል።',
    en: 'This category has no attributes of its own. It still inherits any from parent categories.',
  },
  'categories.attr.errorBody': { am: 'ባህሪያትን መጫን አልተቻለም።', en: 'Could not load attributes.' },
  'categories.attr.col.label': { am: 'ስያሜ', en: 'Label' },
  'categories.attr.col.type': { am: 'አይነት', en: 'Type' },
  'categories.attr.col.order': { am: 'ቅደም ተከተል', en: 'Order' },
  'categories.attr.col.required': { am: 'ግዴታ', en: 'Required' },
  'categories.attr.col.actions': { am: 'ድርጊቶች', en: 'Actions' },
  'categories.attr.requiredYes': { am: 'አዎ', en: 'Yes' },
  'categories.attr.requiredNo': { am: 'አይ', en: 'No' },
  'categories.attr.edit': { am: 'አስተካክል', en: 'Edit' },
  'categories.attr.deactivate': { am: 'አግድ', en: 'Deactivate' },
  'categories.attr.deactivatedToast': { am: 'ባህሪው ታግዷል', en: 'Attribute deactivated' },
  'categories.attr.createdToast': { am: 'ባህሪው ተፈጥሯል', en: 'Attribute created' },
  'categories.attr.updatedToast': { am: 'ባህሪው ተስተካክሏል', en: 'Attribute updated' },
  'categories.attr.deactivateConfirmTitle': {
    am: '{name}ን ላግድ?',
    en: 'Deactivate {name}?',
  },
  'categories.attr.deactivateConfirmBody': {
    am: 'ይህ ባህሪ ከአዳዲስ ምርት ቅጾች ይወገዳል። አስቀድሞ የተመዘገቡ የምርት እሴቶች አይነኩም።',
    en: 'This attribute is removed from new product forms. Already-recorded product values are untouched.',
  },
  'categories.attr.inputType.text': { am: 'ጽሑፍ', en: 'Text' },
  'categories.attr.inputType.number': { am: 'ቁጥር', en: 'Number' },
  'categories.attr.inputType.select': { am: 'ምርጫ', en: 'Select' },
  'categories.attr.inputType.boolean': { am: 'አዎ/አይ', en: 'Yes / No' },
  'categories.attr.form.createTitle': { am: 'አዲስ ባህሪ', en: 'New attribute' },
  'categories.attr.form.editTitle': { am: 'ባህሪ አስተካክል', en: 'Edit attribute' },
  'categories.attr.form.keyLabel': { am: 'ቁልፍ', en: 'Key' },
  'categories.attr.form.keyHint': {
    am: 'snake_case፣ በትንሽ ፊደል የሚጀምር። ከተፈጠረ በኋላ አይለወጥም።',
    en: 'snake_case, starting with a lowercase letter. Cannot be changed after creation.',
  },
  'categories.attr.form.keyInvalid': {
    am: 'ቁልፉ snake_case መሆን አለበት (ለምሳሌ screen_size)።',
    en: 'The key must be snake_case, e.g. screen_size.',
  },
  'categories.attr.form.labelLabel': { am: 'ስያሜ (እንግሊዝኛ)', en: 'Label (English)' },
  'categories.attr.form.labelAmLabel': { am: 'ስያሜ (አማርኛ)', en: 'Label (Amharic)' },
  'categories.attr.form.labelRequired': {
    am: 'የእንግሊዝኛ ስያሜ ያስፈልጋል።',
    en: 'An English label is required.',
  },
  'categories.attr.form.inputTypeLabel': { am: 'የግቤት አይነት', en: 'Input type' },
  'categories.attr.form.sortOrderLabel': { am: 'ቅደም ተከተል', en: 'Order' },
  'categories.attr.form.requiredLabel': { am: 'ግዴታ ነው', en: 'Required' },
  'categories.attr.form.optionsTitle': { am: 'የምርጫ አማራጮች', en: 'Select options' },
  'categories.attr.form.optionsRequired': {
    am: 'ቢያንስ አንድ አማራጭ (እሴትና ስያሜ) ያስፈልጋል።',
    en: 'At least one option (value and label) is required.',
  },
  'categories.attr.form.optionValue': { am: 'እሴት', en: 'Value' },
  'categories.attr.form.optionLabel': { am: 'ስያሜ (እንግሊዝኛ)', en: 'Label (English)' },
  'categories.attr.form.optionLabelAm': { am: 'ስያሜ (አማርኛ)', en: 'Label (Amharic)' },
  'categories.attr.form.optionAdd': { am: 'አማራጭ ጨምር', en: 'Add option' },
  'categories.attr.form.optionRemove': { am: 'አማራጭ አስወግድ', en: 'Remove option' },
  'categories.attr.form.cancel': { am: 'ይቅር', en: 'Cancel' },
  'categories.attr.form.submitCreate': { am: 'ባህሪ ፍጠር', en: 'Create attribute' },
  'categories.attr.form.submitEdit': { am: 'ለውጦችን አስቀምጥ', en: 'Save changes' },
  'categories.attr.form.submitting': { am: 'በማስቀመጥ ላይ...', en: 'Saving...' },

  // --- Product brands section (pages/CategoriesPage — flat, not scoped to
  // a category, so it is its own section rather than part of
  // AttributesEditor).
  'categories.brands.title': { am: 'የምርት ብራንዶች', en: 'Product brands' },
  'categories.brands.subtitle': {
    am: 'በምርት ቅጹ ላይ ለሚታየው ብራንድ ምርጫ የሚያገለግል ዝርዝር። ይህ ዝርዝር በምድብ አይከፈልም።',
    en: 'The managed list behind the brand dropdown on the product form. Not scoped to any category.',
  },
  'categories.brands.newBrand': { am: 'ብራንድ ጨምር', en: 'Add brand' },
  'categories.brands.nameLabel': { am: 'ብራንድ ስም', en: 'Brand name' },
  'categories.brands.add': { am: 'ጨምር', en: 'Add' },
  'categories.brands.adding': { am: 'በመጨመር ላይ...', en: 'Adding...' },
  'categories.brands.cancel': { am: 'ይቅር', en: 'Cancel' },
  'categories.brands.createdToast': { am: 'ብራንድ ተፈጥሯል', en: 'Brand created' },
  'categories.brands.nameRequired': { am: 'ብራንድ ስም ያስፈልጋል።', en: 'Brand name is required.' },
  'categories.brands.col.name': { am: 'ስም', en: 'Name' },
  'categories.brands.col.actions': { am: 'ድርጊቶች', en: 'Actions' },
  'categories.brands.deactivate': { am: 'አቦዝን', en: 'Deactivate' },
  'categories.brands.deactivating': { am: 'በማቦዘን ላይ...', en: 'Deactivating...' },
  'categories.brands.deactivatedToast': { am: 'ብራንድ ተሰናክሏል', en: 'Brand deactivated' },
  'categories.brands.deactivateConfirmTitle': {
    am: '"{name}"ን ላቦዝን?',
    en: 'Deactivate “{name}”?',
  },
  'categories.brands.deactivateConfirmBody': {
    am: 'ይህ ብራንድ ከምርት ቅጹ ምርጫ ዝርዝር ይወገዳል። አስቀድሞ ይህን ብራንድ የተጠቀሙ ምርቶች አይነኩም።',
    en: 'This brand leaves the product form’s selectable list. Products already using it are untouched.',
  },
  'categories.brands.empty': { am: 'ገና ምንም ብራንድ የለም', en: 'No brands yet' },
  'categories.brands.emptyBody': {
    am: 'ገና ምንም ብራንድ አልተመዘገበም። "ብራንድ ጨምር" ብለው የመጀመሪያውን ያክሉ።',
    en: 'No brands have been added yet. Add the first one with "Add brand".',
  },
  'categories.brands.errorBody': {
    am: 'የብራንዶች ዝርዝር መጫን አልተቻለም።',
    en: 'Could not load the brand list.',
  },

  // --- Brand image upload (per-row control, POST
  // /admin/catalog/brands/:id/image) — same key shape as catalog.form.photo*
  // above, this entity's own namespace per this file's per-entity-consistency
  // convention (see error.PRODUCT_BRAND_ALREADY_INACTIVE's own comment).
  // NEEDS NATIVE-SPEAKER REVIEW: am values below are a reasonable attempt
  // matching this section's register, not yet checked by a native speaker.
  'categories.brands.photoAltText': { am: 'የብራንድ ፎቶ', en: 'Brand photo' },
  'categories.brands.photoNone': { am: 'ፎቶ የለም', en: 'No photo' },
  'categories.brands.photoUpload': { am: 'ፎቶ ስቀል', en: 'Upload photo' },
  'categories.brands.photoUploading': { am: 'በመስቀል ላይ...', en: 'Uploading...' },
  'categories.brands.photoUploadedToast': { am: 'የብራንድ ፎቶ ተጭኗል', en: 'Brand photo uploaded' },
  'categories.brands.photoHint': {
    am: 'JPEG፣ PNG ወይም WebP፣ እስከ 8ሜባ ድረስ።',
    en: 'JPEG, PNG, or WebP, up to 8MB.',
  },
  'categories.brands.photoInvalidType': {
    am: 'JPEG፣ PNG ወይም WebP ፋይል ብቻ ተቀባይነት አለው።',
    en: 'Only JPEG, PNG, or WebP files are accepted.',
  },
  'categories.brands.photoTooLarge': {
    am: 'ፎቶው ከ8ሜባ መብለጥ አይችልም።',
    en: 'The photo cannot exceed 8MB.',
  },
  'categories.brands.col.photo': { am: 'ፎቶ', en: 'Photo' },

  // --- Admin "Settings" (pages/SettingsPage, admin-only) ------------------
  'settings.title': { am: 'ቅንብሮች', en: 'Settings' },
  'settings.subtitle': {
    am: 'የስርዓቱን አጠቃላይ ቅንብሮች ያዘምኑ። ካልተለወጡ በስተቀር በአዳዲስ ጥያቄዎች ላይ ተግባራዊ ይሆናሉ።',
    en: 'Update system-wide defaults. These apply to new requests unless overridden elsewhere.',
  },

  'settings.section.company': { am: 'የድርጅት መረጃ', en: 'Company information' },
  'settings.section.quotationDefaults': { am: 'የዋጋ ማቅረቢያ ነባሪዎች', en: 'Quotation defaults' },
  'settings.section.notificationDefaults': {
    am: 'ነባሪ የማሳወቂያ መንገዶች',
    en: 'Default notification channels',
  },
  'settings.section.importMatching': {
    am: 'ከኤክሴል ሲገባ የሚደረግ ግጥሚያ',
    en: 'Excel import matching',
  },
  'settings.section.verification': { am: 'የፕሮፎርማ ማረጋገጫ', en: 'Proforma verification' },
  'settings.section.security': { am: 'ደህንነት', en: 'Security' },
  'settings.section.twoFactor': {
    am: 'ባለሁለት-ደረጃ ማረጋገጫ (2FA)',
    en: 'Two-factor authentication (2FA)',
  },

  // --- Proforma branding: signature + stamp images (components/
  // BrandAssetUpload.jsx). Private, admin-only assets — CLAUDE.md rule #7.
  'settings.section.branding': { am: 'የፕሮፎርማ ማህተምና ፊርማ', en: 'Proforma branding' },
  'settings.branding.hint': {
    am: 'የተፈቀደ ፊርማ እና የድርጅት ማህተም — በእያንዳንዱ የተፈረመ ፕሮፎርማ ላይ ይታተማሉ። ግልጽ ዳራ ያለው PNG ወይም JPG። እነዚህ ምስሎች በግል ማከማቻ ውስጥ ይቀመጣሉ፣ ለገዥ ወይም ለሽያጭ ሰራተኛ በጭራሽ አይታዩም።',
    en: 'The authorised signature and company stamp — they print on every signed proforma. PNG (ideally with transparency) or JPG. These images are held in private storage and are never shown to a buyer or a sales user.',
  },
  'settings.branding.errorBody': {
    am: 'የማህተምና ፊርማ ምስሎችን መጫን አልተቻለም።',
    en: 'Could not load the signature and stamp images.',
  },
  'settings.branding.recommended': {
    am: 'የሚመከር መጠን፡ {width}×{height} ፒክሰል',
    en: 'Recommended size: {width}×{height} px',
  },
  'settings.branding.meta': {
    am: '{format} · {width}×{height} ፒክሰል',
    en: '{format} · {width}×{height} px',
  },
  'settings.branding.alphaWarning': {
    am: 'ይህ PNG ግልጽ ንብርብር (alpha) የለውም — በፕሮፎርማው ላይ ነጭ ሳጥን ይታተማል። ግልጽ ዳራ ያለው ፋይል ይጠቀሙ።',
    en: 'This PNG has no alpha channel — a white box will print over the proforma. Use a file with a transparent background.',
  },
  'settings.branding.jpgNote': {
    am: 'JPG ግልጽነትን አይደግፍም — ጠንካራ ዳራ ይታተማል። ለፊርማ ግልጽ ዳራ ያለው PNG ይመረጣል።',
    en: 'JPG has no transparency — a solid background will print. A transparent PNG is preferable for a signature.',
  },
  'settings.branding.upload': { am: 'ምስል ስቀል', en: 'Upload image' },
  'settings.branding.replace': { am: 'ተካ', en: 'Replace' },
  'settings.branding.remove': { am: 'አስወግድ', en: 'Remove' },
  'settings.branding.replaceConfirmBody': {
    am: 'አዲሱ ምስል ወዲያውኑ ተግባራዊ ይሆናል እና በሚቀጥሉት ፕሮፎርማዎች ላይ ይታተማል።',
    en: 'The new image takes effect immediately and prints on every proforma from now on.',
  },
  'settings.branding.replaceConfirm': { am: 'ፋይል ምረጥ', en: 'Choose a file' },
  'settings.branding.removeConfirmBody': {
    am: 'ይህን ምስል ማስወገድ በፕሮፎርማዎች ላይ የቦታ ማስያዣ ሳጥን እንዲታተም ያደርጋል፣ አዲስ እስኪሰቀል ድረስ።',
    en: 'Removing this image means proformas print a placeholder box until a new one is uploaded.',
  },
  'settings.branding.removeConfirm': { am: 'አስወግድ', en: 'Remove' },

  'settings.branding.signature.title': { am: 'የተፈቀደ ፊርማ', en: 'Authorised signature' },
  'settings.branding.signature.savedToast': { am: 'ፊርማ ተቀምጧል', en: 'Signature saved' },
  'settings.branding.signature.removedToast': { am: 'ፊርማ ተወግዷል', en: 'Signature removed' },
  'settings.branding.signature.replaceConfirmTitle': {
    am: 'ፊርማውን ልተካ?',
    en: 'Replace the signature?',
  },
  'settings.branding.signature.removeConfirmTitle': {
    am: 'ፊርማውን ላስወግድ?',
    en: 'Remove the signature?',
  },
  'settings.branding.signature.previewLightAlt': {
    am: 'ፊርማ በነጭ ዳራ ላይ',
    en: 'Signature on a light background',
  },
  'settings.branding.signature.previewDarkAlt': {
    am: 'ፊርማ በጨለማ ዳራ ላይ',
    en: 'Signature on a dark background',
  },

  'settings.branding.stamp.title': { am: 'የድርጅት ማህተም', en: 'Company stamp' },
  'settings.branding.stamp.savedToast': { am: 'ማህተም ተቀምጧል', en: 'Stamp saved' },
  'settings.branding.stamp.removedToast': { am: 'ማህተም ተወግዷል', en: 'Stamp removed' },
  'settings.branding.stamp.replaceConfirmTitle': { am: 'ማህተሙን ልተካ?', en: 'Replace the stamp?' },
  'settings.branding.stamp.removeConfirmTitle': { am: 'ማህተሙን ላስወግድ?', en: 'Remove the stamp?' },
  'settings.branding.stamp.previewLightAlt': {
    am: 'ማህተም በነጭ ዳራ ላይ',
    en: 'Stamp on a light background',
  },
  'settings.branding.stamp.previewDarkAlt': {
    am: 'ማህተም በጨለማ ዳራ ላይ',
    en: 'Stamp on a dark background',
  },

  'error.INVALID_BRAND_ASSET': {
    am: 'ይህ ፋይል ትክክለኛ PNG ወይም JPG ምስል አይደለም።',
    en: 'That file is not a valid PNG or JPG image.',
  },
  'error.INVALID_BRAND_ASSET_TYPE': {
    am: 'PNG ወይም JPG ምስሎች ብቻ ተቀባይነት አላቸው።',
    en: 'Only PNG or JPG images are accepted.',
  },
  'error.BRAND_ASSET_NOT_FOUND': {
    am: 'ምንም ምስል አልተዋቀረም።',
    en: 'No image is configured.',
  },
  'error.BRAND_ASSET_TOO_LARGE': {
    am: 'ምስሉ ከ2ሜባ መብለጥ አይችልም።',
    en: 'The image cannot exceed 2MB.',
  },

  'settings.field.companyName.label': { am: 'የድርጅት ስም', en: 'Company name' },
  'settings.field.companyTin.label': {
    am: 'የግብር መለያ ቁጥር (TIN)',
    en: 'Tax Identification Number (TIN)',
  },
  'settings.field.companyTagline.label': {
    am: 'የድርጅት መፈክር',
    en: 'Company tagline',
  },
  'settings.field.commercialRegistrationNumber.label': {
    am: 'የንግድ ምዝገባ ቁጥር',
    en: 'Commercial registration number',
  },
  'settings.field.companyAddress.label': {
    am: 'የድርጅት አድራሻ',
    en: 'Company address',
  },
  'settings.field.companyPhone.label': {
    am: 'ስልክ ቁጥር',
    en: 'Phone number',
  },
  'settings.field.companyEmail.label': {
    am: 'ኢሜይል',
    en: 'Email',
  },
  'settings.field.companyOpeningHours.label': {
    am: 'የስራ ሰዓታት',
    en: 'Opening hours',
  },
  'settings.field.vatPercent.label': {
    am: 'የተጨማሪ እሴት ታክስ መቶኛ (%)',
    en: 'VAT percentage (%)',
  },
  'settings.field.quoteValidityDays.label': {
    am: 'የዋጋ ማቅረቢያ የሚፀናበት ቀናት ብዛት (ቀናት)',
    en: 'Quote validity (days)',
  },
  'settings.field.defaultDeliveryDays.label': {
    am: 'ነባሪ የማድረሻ ጊዜ (ቀናት)',
    en: 'Default delivery time (days)',
  },
  'settings.field.defaultWarrantyMonths.label': {
    am: 'ነባሪ የዋስትና ጊዜ (ወራት)',
    en: 'Default warranty (months)',
  },
  'settings.field.defaultPaymentTerms.label': {
    am: 'ነባሪ የክፍያ ውሎች',
    en: 'Default payment terms',
  },
  'settings.field.notifyEmailDefault.label': {
    am: 'ነባሪ የኢሜይል ማሳወቂያ',
    en: 'Default: notify by email',
  },
  'settings.field.notifyTelegramDefault.label': {
    am: 'ነባሪ የቴሌግራም ማሳወቂያ',
    en: 'Default: notify by Telegram',
  },
  'settings.field.notifyWhatsappDefault.label': {
    am: 'ነባሪ የዋትስአፕ ማሳወቂያ',
    en: 'Default: notify by WhatsApp',
  },
  'settings.field.matchConfidenceFloor.label': {
    am: 'ዝቅተኛ የተዛማጅነት እርግጠኝነት ደረጃ (0 እስከ 1)',
    en: 'Match confidence floor (0 to 1)',
  },
  'settings.field.proformaShowQr.label': {
    am: 'በፕሮፎርማ PDF ላይ የQR ኮድ አሳይ',
    en: 'Show QR code on proforma PDFs',
  },
  'settings.field.loginAttemptCap.label': {
    am: 'ከመቆለፍ በፊት የሚፈቀዱ ያልተሳኩ የመግቢያ ሙከራዎች',
    en: 'Failed login attempts allowed before lockout',
  },
  'settings.field.loginLockoutMinutes.label': {
    am: 'የመቆለፊያ ጊዜ (ደቂቃዎች)',
    en: 'Lockout duration (minutes)',
  },
  'settings.field.sessionIdleHours.label': {
    am: 'የክፍለ ጊዜ የስራ ፈት ማብቂያ (ሰዓቶች)',
    en: 'Session idle timeout (hours)',
  },
  'settings.field.sessionAbsoluteHours.label': {
    am: 'የክፍለ ጊዜ ፍጹም ማብቂያ (ሰዓቶች)',
    en: 'Session absolute timeout (hours)',
  },
  'settings.field.trackingLinkGraceDays.label': {
    am: 'ከዋጋ ማቅረቢያ ማብቂያ በኋላ ያለ የክትትል አገናኝ የችሮታ ጊዜ (ቀናት)',
    en: 'Tracking-link grace period after quote expiry (days)',
  },

  'settings.notificationDefaults.hint': {
    am: 'እያንዳንዱ ድርጅት የራሱን የማሳወቂያ ምርጫ ማስቀመጥ ይችላል፤ ከዚህ በታች ያሉት ያ ምርጫ ባልተቀመጠ ጊዜ ብቻ የሚያገለግሉ ነባሪዎች ናቸው።',
    en: "Each organization can set its own notification preference. These are only the fallback used when an organization hasn't set one.",
  },
  'settings.field.matchConfidenceFloor.hint': {
    am: 'ከኤክሴል የገቡ መስመሮች ካታሎግ ውስጥ ካለ ምርት ጋር ራሱ በራሱ የሚዛመዱት የተዛማጅነት ውጤታቸው ከዚህ ደረጃ በላይ ወይም እኩል ሲሆን ብቻ ነው፤ ከዚህ በታች ያሉ መስመሮች በእጅ መዛመድ ያስፈልጋቸዋል።',
    en: 'A row imported from Excel only auto-matches to a catalog product when its fuzzy-match score meets or exceeds this floor. Anything below it needs manual matching — that is a normal outcome, not an error.',
  },
  'settings.field.proformaShowQr.hint': {
    am: 'በሚፈቀድበት ጊዜ፣ ከዚህ በኋላ የሚዘጋጁ እያንዳንዱ የተፈረመ ፕሮፎርማ PDF የትኛውም ሰው የፕሮፎርማውን ትክክለኛ አሃዞችና ማፅደቅ ሁኔታ ማየት የሚችልበትን የህዝብ ማረጋገጫ ገፅ (/verify) የሚያገናኝ QR ኮድ ይይዛል። ይህን ማጥፋት የፕሮፎርማ ቁጥር በመጻፍ ወደዚያ ገፅ የመድረስ እድልን አያስቀርም፤ አስቀድመው የወጡ ፕሮፎርማዎችም ላይ ተፅዕኖ አያደርግም — እነርሱ ገንብተው የነበሩትን QR ሁኔታ ይዘው ይቀራሉ።',
    en: 'When on, every proforma PDF rendered from now on carries a QR code linking to the public /verify page, where anyone can see the real figures and approval status. Turning it off does not remove the ability to reach that page by typing a proforma number, and it never affects proformas already issued — they keep whichever QR state they were rendered with.',
  },

  'settings.saveButton': { am: 'ለውጦችን አስቀምጥ', en: 'Save changes' },
  'settings.saving': { am: 'በማስቀመጥ ላይ...', en: 'Saving...' },
  'settings.savedFlash': { am: 'ተቀምጧል', en: 'Saved' },

  'settings.error.companyNameTooLong': {
    am: 'የድርጅት ስም ከ200 ቁምፊዎች መብለጥ የለበትም።',
    en: 'Company name must be 200 characters or fewer.',
  },
  'settings.error.companyTinTooLong': {
    am: 'የግብር መለያ ቁጥር ከ20 ቁምፊዎች መብለጥ የለበትም።',
    en: 'TIN must be 20 characters or fewer.',
  },
  'settings.error.companyTaglineTooLong': {
    am: 'የድርጅት መፈክር ከ200 ቁምፊዎች መብለጥ የለበትም።',
    en: 'Company tagline must be 200 characters or fewer.',
  },
  'settings.error.commercialRegistrationNumberTooLong': {
    am: 'የንግድ ምዝገባ ቁጥር ከ40 ቁምፊዎች መብለጥ የለበትም።',
    en: 'Commercial registration number must be 40 characters or fewer.',
  },
  'settings.error.companyAddressTooLong': {
    am: 'የድርጅት አድራሻ ከ255 ቁምፊዎች መብለጥ የለበትም።',
    en: 'Company address must be 255 characters or fewer.',
  },
  'settings.error.companyPhoneTooLong': {
    am: 'ስልክ ቁጥር ከ30 ቁምፊዎች መብለጥ የለበትም።',
    en: 'Phone number must be 30 characters or fewer.',
  },
  'settings.error.companyEmailTooLong': {
    am: 'ኢሜይል ከ190 ቁምፊዎች መብለጥ የለበትም።',
    en: 'Email must be 190 characters or fewer.',
  },
  'settings.error.companyOpeningHoursTooLong': {
    am: 'የስራ ሰዓታት ከ120 ቁምፊዎች መብለጥ የለበትም።',
    en: 'Opening hours must be 120 characters or fewer.',
  },
  'settings.error.defaultPaymentTermsTooLong': {
    am: 'ነባሪ የክፍያ ውሎች ከ255 ቁምፊዎች መብለጥ የለበትም።',
    en: 'Default payment terms must be 255 characters or fewer.',
  },
  'settings.error.vatPercentRange': {
    am: 'የተጨማሪ እሴት ታክስ መቶኛ ከ0 እስከ 100 መካከል መሆን አለበት።',
    en: 'VAT percentage must be between 0 and 100.',
  },
  'settings.error.quoteValidityDaysRange': {
    am: 'የዋጋ ማቅረቢያ የሚፀናበት ቀናት ከ1 እስከ 365 ባለ ሙሉ ቁጥር መሆን አለበት።',
    en: 'Quote validity must be a whole number of days between 1 and 365.',
  },
  'settings.error.defaultDeliveryDaysRange': {
    am: 'ነባሪ የማድረሻ ጊዜ ከ0 እስከ 365 ባለ ሙሉ ቁጥር መሆን አለበት።',
    en: 'Default delivery time must be a whole number of days between 0 and 365.',
  },
  'settings.error.defaultWarrantyMonthsRange': {
    am: 'ነባሪ የዋስትና ጊዜ ከ0 እስከ 120 ባለ ሙሉ ቁጥር መሆን አለበት።',
    en: 'Default warranty must be a whole number of months between 0 and 120.',
  },
  'settings.error.matchConfidenceFloorRange': {
    am: 'ዝቅተኛ የተዛማጅነት እርግጠኝነት ደረጃ ከ0 እስከ 1 መካከል መሆን አለበት።',
    en: 'Match confidence floor must be between 0 and 1.',
  },
  'settings.error.loginAttemptCapRange': {
    am: 'የመግቢያ ሙከራዎች ብዛት ከ1 እስከ 20 ባለ ሙሉ ቁጥር መሆን አለበት።',
    en: 'Login attempt cap must be a whole number between 1 and 20.',
  },
  'settings.error.loginLockoutMinutesRange': {
    am: 'የመቆለፊያ ጊዜ ከ1 እስከ 1440 ደቂቃ ባለ ሙሉ ቁጥር መሆን አለበት።',
    en: 'Lockout duration must be a whole number of minutes between 1 and 1440.',
  },
  'settings.error.sessionIdleHoursRange': {
    am: 'የክፍለ ጊዜ የስራ ፈት ማብቂያ ከ1 እስከ 168 ሰዓት ባለ ሙሉ ቁጥር መሆን አለበት።',
    en: 'Session idle timeout must be a whole number of hours between 1 and 168.',
  },
  'settings.error.sessionAbsoluteHoursRange': {
    am: 'የክፍለ ጊዜ ፍጹም ማብቂያ ከ1 እስከ 720 ሰዓት ባለ ሙሉ ቁጥር መሆን አለበት።',
    en: 'Session absolute timeout must be a whole number of hours between 1 and 720.',
  },
  'settings.error.trackingLinkGraceDaysRange': {
    am: 'የክትትል አገናኝ የችሮታ ጊዜ ከ0 እስከ 365 ቀናት ባለ ሙሉ ቁጥር መሆን አለበት።',
    en: 'Tracking-link grace period must be a whole number of days between 0 and 365.',
  },

  'settings.errorBody': { am: 'ቅንብሮችን መጫን አልተቻለም።', en: 'Could not load settings.' },

  // --- Admin self-service 2FA (components/TwoFactorSettings.jsx) ----------
  'settings.twoFactor.hint': {
    am: 'ለአስተዳዳሪ መለያዎ ተጨማሪ ጥበቃ ይጨምሩ። ስልክዎ ላይ ካለ የማረጋገጫ መተግበሪያ ጋር ይሰራል።',
    en: 'Add an extra layer of protection to your admin account. Works with any authenticator app on your phone.',
  },
  'settings.twoFactor.status.enabled': {
    am: 'ባለሁለት-ደረጃ ማረጋገጫ ነቅቷል',
    en: 'Two-factor authentication is enabled',
  },
  'settings.twoFactor.status.disabled': {
    am: 'ባለሁለት-ደረጃ ማረጋገጫ አልነቃም',
    en: 'Two-factor authentication is not enabled',
  },
  'settings.twoFactor.enable.button': { am: 'አንቃ', en: 'Enable 2FA' },
  'settings.twoFactor.enable.starting': { am: 'በመጀመር ላይ...', en: 'Starting...' },
  'settings.twoFactor.setup.instructions': {
    am: 'ይህን QR ኮድ በማረጋገጫ መተግበሪያዎ (ለምሳሌ Google Authenticator) ይቃኙ፣ ወይም ሚስጥሩን በእጅ ያስገቡ፣ ከዚያም ከመተግበሪያው የሚያገኙትን ኮድ ያስገቡ።',
    en: 'Scan this QR code with your authenticator app (e.g. Google Authenticator), or enter the secret manually, then type the code it shows you.',
  },
  'settings.twoFactor.setup.qrAlt': {
    am: 'ለማረጋገጫ መተግበሪያ ማዋቀሪያ QR ኮድ',
    en: 'QR code for authenticator app setup',
  },
  'settings.twoFactor.setup.codeLabel': { am: 'የ6-አሃዝ ኮድ', en: '6-digit code' },
  'settings.twoFactor.setup.confirm': { am: 'አረጋግጥና አንቃ', en: 'Confirm and enable' },
  'settings.twoFactor.setup.confirming': { am: 'በማረጋገጥ ላይ...', en: 'Confirming...' },
  'settings.twoFactor.setup.cancel': { am: 'ይቅር', en: 'Cancel' },
  'settings.twoFactor.error.invalidCode': { am: 'ኮዱ ትክክል አልነበረም።', en: 'That code did not match.' },
  'settings.twoFactor.error.noPendingSetup': {
    am: 'ማዋቀሩ ጊዜው አልፎበታል፤ እባክዎ እንደገና ይጀምሩ።',
    en: 'Setup expired. Please start again.',
  },
  'settings.twoFactor.error.wrongPassword': {
    am: 'የይለፍ ቃል ትክክል አይደለም።',
    en: 'Incorrect password.',
  },
  'settings.twoFactor.recoveryCodes.warning': {
    am: 'እነዚህን መልሶ ማግኛ ኮዶች በደህና ቦታ ያስቀምጡ። ስልክዎን ካጡ ወደ መለያዎ ለመግባት እያንዳንዱን አንድ ጊዜ ብቻ መጠቀም ይችላሉ። እንደገና አይታዩም።',
    en: "Save these recovery codes somewhere safe. Each can be used once to sign in if you lose your phone. They won't be shown again.",
  },
  'settings.twoFactor.recoveryCodes.acknowledge': {
    am: 'አስቀምጫቸዋለሁ',
    en: "I've saved these codes",
  },
  'settings.twoFactor.disable.button': { am: 'አሰናክል', en: 'Disable 2FA' },
  'settings.twoFactor.disable.passwordLabel': {
    am: 'ለማረጋገጥ የአሁኑን የይለፍ ቃል ያስገቡ',
    en: 'Enter your current password to confirm',
  },
  'settings.twoFactor.disable.confirm': { am: 'አሰናክልን አረጋግጥ', en: 'Confirm disable' },
  'settings.twoFactor.disable.disabling': { am: 'በማሰናከል ላይ...', en: 'Disabling...' },
  'settings.twoFactor.disable.cancel': { am: 'ይቅር', en: 'Cancel' },

  // --- Admin approval decision screen (pages/ApprovalDetailPage) ----------
  'approvalDetail.backToQueue': { am: '← ወደ ማጽደቅ ወረፋ ተመለስ', en: '← Back to approval queue' },
  'approvalDetail.errorBody': {
    am: 'ይህን የዋጋ ማቅረቢያ መጫን አልተቻለም።',
    en: 'Could not load this quotation.',
  },
  'approvalDetail.request': { am: 'ጥያቄ', en: 'Request' },
  'approvalDetail.organization': { am: 'ድርጅት', en: 'Organization' },
  'approvalDetail.pricedBy': { am: 'ዋጋ የሰጠው/ችው', en: 'Priced by' },
  'approvalDetail.submittedAt': { am: 'የገባበት ቀን', en: 'Submitted' },
  'approvalDetail.pricedByUnknown': { am: 'አይታወቅም', en: 'Unknown' },

  'approvalDetail.belowFloor.title': {
    am: 'ከዝቅተኛ ዋጋ በታች ያሉ መስመሮች',
    en: 'Below-floor lines',
  },
  'approvalDetail.belowFloor.none': {
    am: 'ከዝቅተኛ ዋጋ በታች ያለ መስመር የለም — ሁሉም መስመሮች በዝቅተኛ ዋጋ ወይም ከዚያ በላይ ተይዘዋል።',
    en: 'No below-floor lines — every line is priced at or above floor.',
  },
  'approvalDetail.belowFloor.reason': { am: 'የቅናሽ ምክንያት፦', en: 'Discount reason:' },
  'approvalDetail.belowFloor.priceVsFloor': {
    am: '{price} (ዝቅተኛ ዋጋ {floor})',
    en: '{price} (floor is {floor})',
  },
  'approvalDetail.belowFloor.clear': { am: 'አጽድቅ', en: 'Clear' },
  'approvalDetail.belowFloor.clearing': { am: 'በማጽደቅ ላይ...', en: 'Clearing...' },
  'approvalDetail.belowFloor.cleared': {
    am: 'በ{name} ጸድቋል፣ {date}',
    en: 'Cleared by {name}, {date}',
  },
  'approvalDetail.belowFloor.clearedUnknown': { am: 'ያልታወቀ አስተዳዳሪ', en: 'an admin' },

  'approvalDetail.margin.title': { am: 'ትርፍ ህዳግ', en: 'Margin' },
  'approvalDetail.margin.overall': { am: 'አጠቃላይ ትርፍ ህዳግ', en: 'Overall margin' },
  'approvalDetail.margin.perLine': { am: 'ለእያንዳንዱ መስመር', en: 'Per line' },
  'approvalDetail.margin.notAvailable': { am: 'አይታወቅም', en: 'not available' },
  'approvalDetail.margin.notAvailableHint': {
    am: 'ለአንድ ወይም ከዚያ በላይ መስመሮች የግዢ ዋጋ ስላልተመዘገበ አጠቃላይ ትርፍ ህዳግ ሊሰላ አልቻለም።',
    en: "Overall margin can't be computed — one or more lines has no cost on file.",
  },

  'approvalDetail.history.title': { am: 'ያለፉ ዋጋዎች', en: 'Price history' },

  // --- Document access log (security-hardening task, item 6) --------------
  'approvalDetail.documentAccessLog.toggleShow': {
    am: 'የሰነድ ተደራሽነት ምዝግብ ማስታወሻ አሳይ',
    en: 'Show document access log',
  },
  'approvalDetail.documentAccessLog.toggleHide': { am: 'ደብቅ', en: 'Hide' },
  'approvalDetail.documentAccessLog.empty': {
    am: 'ለዚህ ጥያቄ እስካሁን የተከፈተ ሰነድ የለም።',
    en: 'No document has been opened for this request yet.',
  },
  'approvalDetail.documentAccessLog.documentType.quotation_pdf': {
    am: 'የዋጋ ማቅረቢያ PDF',
    en: 'Quotation PDF',
  },
  'approvalDetail.documentAccessLog.documentType.payment_request_pdf': {
    am: 'የክፍያ ጥያቄ PDF',
    en: 'Payment request PDF',
  },
  'approvalDetail.documentAccessLog.documentType.quotation_view': {
    am: 'የዋጋ ማቅረቢያ ገጽ ታይቷል',
    en: 'Quotation page viewed',
  },
  'approvalDetail.documentAccessLog.accessorType.admin': {
    am: 'በአስተዳዳሪ ({name}) ተከፍቷል',
    en: 'Opened by admin {name}',
  },
  'approvalDetail.documentAccessLog.accessorType.sales': {
    am: 'በሽያጭ ሰራተኛ ({name}) ተከፍቷል',
    en: 'Opened by sales rep {name}',
  },
  'approvalDetail.documentAccessLog.accessorType.guest_token': {
    am: 'በደንበኛ በእንግዳ አገናኝ ተከፍቷል',
    en: 'Opened by the customer via a guest link',
  },
  'approvalDetail.documentAccessLog.accessorType.tracking_token': {
    am: 'በደንበኛ በክትትል አገናኝ ተከፍቷል',
    en: 'Opened by the customer via their tracking link',
  },

  'approvalDetail.allLines.toggleShow': {
    am: 'ሁሉንም {count} መስመሮች አሳይ',
    en: 'Show all {count} lines',
  },
  'approvalDetail.allLines.toggleHide': { am: 'ደብቅ', en: 'Hide' },
  'approvalDetail.allLines.col.line': { am: '#', en: '#' },
  'approvalDetail.allLines.col.description': { am: 'መግለጫ', en: 'Description' },
  'approvalDetail.allLines.col.qty': { am: 'ብዛት', en: 'Qty' },
  'approvalDetail.allLines.col.unitPrice': { am: 'የነጠላ ዋጋ', en: 'Unit price' },
  'approvalDetail.allLines.col.lineTotal': { am: 'ድምር', en: 'Line total' },
  'approvalDetail.allLines.col.status': { am: 'ሁኔታ', en: 'Status' },
  'approvalDetail.allLines.statusOk': { am: 'መደበኛ', en: 'Normal' },
  'approvalDetail.allLines.statusBelowFloorUncleared': {
    am: 'ከዝቅተኛ ዋጋ በታች — ያልጸደቀ',
    en: 'Below floor — uncleared',
  },
  'approvalDetail.allLines.statusBelowFloorCleared': {
    am: 'ከዝቅተኛ ዋጋ በታች — ጸድቋል',
    en: 'Below floor — cleared',
  },

  'approvalDetail.moreDetails.toggleShow': { am: 'ተጨማሪ ዝርዝሮች', en: 'More details' },
  'approvalDetail.moreDetails.toggleHide': { am: 'ደብቅ', en: 'Hide' },
  'approvalDetail.moreDetails.neededBy': {
    am: 'የሚፈለግ የማድረሻ ቀን',
    en: 'Required delivery date',
  },
  'approvalDetail.moreDetails.buyerNote': { am: 'የገዥ ማስታወሻ', en: "Buyer's note" },
  'approvalDetail.moreDetails.purpose': { am: 'ዓላማ', en: 'Purpose' },
  'approvalDetail.moreDetails.subtotal': { am: 'ንዑስ ድምር', en: 'Subtotal' },
  'approvalDetail.moreDetails.discount': { am: 'ቅናሽ', en: 'Discount' },
  'approvalDetail.moreDetails.freight': { am: 'መላኪያ', en: 'Freight' },
  'approvalDetail.moreDetails.vat': { am: 'ተ.እ.ታ (15%)', en: 'VAT (15%)' },
  'approvalDetail.moreDetails.total': { am: 'ጠቅላላ ድምር', en: 'Total' },

  'approvalDetail.actions.discountPercentLabel': { am: 'ቅናሽ (%)', en: 'Discount (%)' },
  'approvalDetail.actions.freightAmountLabel': {
    am: 'መላኪያ ክፍያ ({currency})',
    en: 'Freight ({currency})',
  },
  // Editable-terms task: admin-screen overrides on OUR document terms.
  // "Our ..." throughout, same convention WorkspacePage.jsx's equivalent
  // fields use, so the two screens never describe the same thing two
  // different ways. buyerAskedForNote deliberately reuses "asked for"
  // phrasing distinct from moreDetails.neededBy's "Required delivery
  // date" label above — this is a contrast note sitting beside OUR field,
  // not a second rendering of the buyer's own label.
  'approvalDetail.actions.deliveryDaysLabel': {
    am: 'የእኛ የማድረሻ ጊዜ (ቀናት)',
    en: 'Our delivery lead time (days)',
  },
  'approvalDetail.actions.buyerAskedForNote': {
    am: 'ገዥው የጠየቀው፦ {date}',
    en: 'Buyer asked for: {date}',
  },
  'approvalDetail.actions.paymentTermsLabel': { am: 'የክፍያ ውሎች', en: 'Payment terms' },
  'approvalDetail.actions.validUntilLabel': {
    am: 'የዋጋ ማቅረቢያ የሚያበቃበት ቀን',
    en: 'Quote valid until',
  },
  'approvalDetail.actions.approve': { am: 'አጽድቅ', en: 'Approve' },
  'approvalDetail.actions.approving': { am: 'በማጽደቅ ላይ...', en: 'Approving...' },
  'approvalDetail.actions.approveDisabledHint': {
    am: 'ከማጽደቅዎ በፊት ከዝቅተኛ ዋጋ በታች ያሉ ሁሉንም መስመሮች ያጽድቁ።',
    en: 'Clear every below-floor line before approving.',
  },
  'approvalDetail.actions.reject': { am: 'ውድቅ አድርግ', en: 'Reject' },
  'approvalDetail.actions.rejecting': { am: 'ውድቅ በማድረግ ላይ...', en: 'Rejecting...' },
  'approvalDetail.actions.rejectReasonLabel': { am: 'የውድቅ ምክንያት', en: 'Rejection reason' },
  'approvalDetail.actions.rejectReasonPlaceholder': {
    am: 'ለምን ውድቅ እንደተደረገ ይግለጹ...',
    en: 'Explain why this is being rejected...',
  },
  'approvalDetail.actions.rejectConfirm': { am: 'ውድቅ ማድረግን አረጋግጥ', en: 'Confirm reject' },
  'approvalDetail.actions.rejectCancel': { am: 'ይቅር', en: 'Cancel' },
  'approvalDetail.decisionMade.approved': {
    am: 'ይህ የዋጋ ማቅረቢያ ጸድቋል፣ ፕሮፎርማ ቁጥር {proformaNo}።',
    en: 'This quotation has been approved as {proformaNo}.',
  },
  'approvalDetail.decisionMade.rejected': {
    am: 'ይህ የዋጋ ማቅረቢያ ውድቅ ተደርጓል፦ {reason}',
    en: 'This quotation was rejected: {reason}',
  },
  'approvalDetail.confirmApprove.title': { am: 'ይህን ማቅረቢያ ላጽድቅ?', en: 'Approve this quotation?' },
  'approvalDetail.confirmApprove.body': {
    am: 'ቋሚ የፕሮፎርማ ቁጥር ይመደባል፣ የተፈረመ PDF ይዘጋጃል። ይህ መቀልበስ አይቻልም።',
    en: 'A permanent proforma number is allocated and the signed PDF is queued. This cannot be undone.',
  },
  // The confirm dialog's totals breakdown, recomputed from the Discount/
  // Freight fields as currently typed (this task's brief: the dialog must
  // never show pre-discount/freight figures). Built as separate lines
  // joined with "\n" by handleApprove — Discount/Freight lines are only
  // included when non-zero, same convention as moreDetails further up this
  // file and drawTotalsStack in jobs/renderQuotationPdf.js — so each needs
  // its own key, not one template with every line baked in.
  'approvalDetail.confirmApprove.lineSubtotal': {
    am: 'ንዑስ ድምር፦ {amount} {currency}',
    en: 'Subtotal: {amount} {currency}',
  },
  'approvalDetail.confirmApprove.lineDiscount': {
    am: 'ቅናሽ ({percent}%)፦ -{amount} {currency}',
    en: 'Discount ({percent}%): -{amount} {currency}',
  },
  'approvalDetail.confirmApprove.lineFreight': {
    am: 'መላኪያ፦ {amount} {currency}',
    en: 'Freight: {amount} {currency}',
  },
  'approvalDetail.confirmApprove.lineVat': {
    am: 'ተ.እ.ታ ({percent}%)፦ {amount} {currency}',
    en: 'VAT ({percent}%): {amount} {currency}',
  },
  'approvalDetail.confirmApprove.lineTotal': {
    am: 'ጠቅላላ ድምር፦ {amount} {currency}',
    en: 'Grand total: {amount} {currency}',
  },
  'approvalDetail.approvedToast': {
    am: 'ጸድቋል — {proformaNo}',
    en: 'Approved — {proformaNo}',
  },
  'approvalDetail.confirmReject.title': { am: 'ይህን ማቅረቢያ ውድቅ ላድርግ?', en: 'Reject this quotation?' },
  'approvalDetail.confirmReject.body': {
    am: 'ማቅረቢያው ለሽያጭ ሰራተኛው ዳግም እንዲተምን ይመለሳል።',
    en: 'The quotation goes back to the salesperson to re-price.',
  },
  'approvalDetail.rejectedToast': { am: 'ማቅረቢያው ውድቅ ተደርጓል', en: 'Quotation rejected' },
  'approvalDetail.belowFloor.clearedToast': {
    am: 'ከወለል በታች መስመር ጸድቷል',
    en: 'Below-floor line cleared',
  },

  // --- Buyer review (migration 016 / proforma-review). Shown only when this
  // quotation is a revision the BUYER triggered by reviewing the proforma
  // line by line (revision > 1 && revision_origin = 'buyer_review'). Surfaces
  // the superseded original's lines with the buyer's per-line decision — old
  // qty beside new, declined lines with the stated reason — plus per-line
  // unit-price edit inputs (approveBodySchema.lineEdits): "a smaller order
  // may not deserve the original unit price". A real price move sets
  // revision_price_changed, which forces admin-only approval for the
  // revision.
  'approvalDetail.buyerReview.title': { am: 'የገዥ ግምገማ', en: 'Buyer review' },
  'approvalDetail.buyerReview.intro': {
    am: 'ገዥው የቀድሞውን ፕሮፎርማ መስመር በመስመር ገምግሞ የተወሰኑትን ተቀብሏል። ይህ ማሻሻያ ({revision}) የተቀበላቸውን መስመሮች ብቻ ይይዛል፤ የነጠላ ዋጋዎች ሳይቀየሩ።',
    en: 'The buyer reviewed the previous proforma line by line and accepted some lines. This revision (r{revision}) carries only the lines they accepted, unit prices unchanged.',
  },
  'approvalDetail.buyerReview.summary': {
    am: '{accepted} ተቀብሏል · {changed} ብዛት ተቀይሯል · {declined} ውድቅ አድርጓል',
    en: '{accepted} accepted · {changed} qty changed · {declined} declined',
  },
  'approvalDetail.buyerReview.col.line': { am: 'መስመር', en: 'Line' },
  'approvalDetail.buyerReview.col.decision': { am: 'የገዥ ውሳኔ', en: "Buyer's decision" },
  'approvalDetail.buyerReview.col.qty': { am: 'ብዛት (የቀድሞ → አዲስ)', en: 'Qty (was → now)' },
  'approvalDetail.buyerReview.col.unitPrice': { am: 'የነጠላ ዋጋ', en: 'Unit price' },
  'approvalDetail.buyerReview.decision.accepted': { am: 'ተቀብሏል', en: 'Accepted' },
  'approvalDetail.buyerReview.decision.qty_changed': { am: 'ብዛት ቀይሯል', en: 'Qty changed' },
  'approvalDetail.buyerReview.decision.declined': { am: 'ውድቅ አድርጓል', en: 'Declined' },
  'approvalDetail.buyerReview.qtyUnchanged': { am: '{qty} (ሳይቀየር)', en: '{qty} (unchanged)' },
  'approvalDetail.buyerReview.qtyChanged': { am: '{was} → {now}', en: '{was} → {now}' },
  'approvalDetail.buyerReview.declinedTitle': {
    am: 'ገዥው ውድቅ ያደረጋቸው መስመሮች',
    en: 'Lines the buyer declined',
  },
  'approvalDetail.buyerReview.declinedNote': { am: 'ማብራሪያ፡ {note}', en: 'Note: {note}' },
  'approvalDetail.buyerReview.priceEditLabel': {
    am: 'የነጠላ ዋጋ አስተካክል (አማራጭ)',
    en: 'Adjust unit price (optional)',
  },
  'approvalDetail.buyerReview.priceEditHint': {
    am: 'የነጠላ ዋጋ መቀየር ይህን ማሻሻያ በአስተዳዳሪ ብቻ እንዲጸድቅ ያደርገዋል። ከዝቅተኛ ዋጋ በታች መውረድ አይቻልም።',
    en: 'Changing a unit price forces admin-only approval for this revision. An edit may not cross the floor.',
  },
  'approvalDetail.buyerReview.priceChanged': { am: 'ተቀይሯል', en: 'changed' },
  'declineReason.price': { am: 'ዋጋ በጣም ከፍ ብሏል', en: 'Price too high' },
  'declineReason.delivery_time': { am: 'የማድረሻ ጊዜ አይመችም', en: 'Delivery time' },
  'declineReason.specification_mismatch': {
    am: 'ዝርዝሩ ከፍላጎት ጋር አይመሳሰልም',
    en: 'Specification mismatch',
  },
  'declineReason.chose_another_supplier': { am: 'ሌላ አቅራቢ መርጧል', en: 'Chose another supplier' },
  'declineReason.budget_cancelled': { am: 'በጀቱ ተሰርዟል', en: 'Budget cancelled' },
  'declineReason.other': { am: 'ሌላ', en: 'Other' },

  // --- Payment (mark-paid) — only shown once the customer has accepted and a
  // payment request exists. Financial record: gated behind the confirm dialog.
  'approvalDetail.payment.title': { am: 'ክፍያ', en: 'Payment' },
  'approvalDetail.payment.amountDue': { am: 'የሚከፈል መጠን', en: 'Amount due' },
  'approvalDetail.payment.status': { am: 'ሁኔታ', en: 'Status' },
  'approvalDetail.payment.statusAwaiting': { am: 'ክፍያ በመጠበቅ ላይ', en: 'Awaiting payment' },
  'approvalDetail.payment.statusPaid': { am: 'ተከፍሏል', en: 'Paid' },
  'approvalDetail.payment.paidNote': {
    am: 'ተከፍሏል — የባንክ ማጣቀሻ {reference}፣ {amount}፣ {date}',
    en: 'Paid — bank reference {reference}, {amount}, on {date}',
  },
  'approvalDetail.payment.bankReferenceLabel': { am: 'የባንክ ማጣቀሻ', en: 'Bank reference' },
  'approvalDetail.payment.paidAtLabel': { am: 'የተከፈለበት ቀን', en: 'Payment date' },
  'approvalDetail.payment.markPaid': { am: 'እንደተከፈለ ምልክት አድርግ', en: 'Mark paid' },
  'approvalDetail.payment.marking': { am: 'ምልክት በማድረግ ላይ...', en: 'Marking...' },
  'approvalDetail.payment.bankReferenceRequired': {
    am: 'የባንክ ማጣቀሻ ያስፈልጋል።',
    en: 'A bank reference is required.',
  },
  'approvalDetail.payment.confirmTitle': {
    am: 'ይህን ክፍያ እንደተቀበሉ ያረጋግጣሉ?',
    en: 'Record this payment as received?',
  },
  'approvalDetail.payment.confirmBody': {
    am: 'ይህ {amount} እንደተከፈለ በቋሚነት ይመዘግባል። ይህ የገንዘብ መዝገብ ነው።',
    en: 'This permanently records {amount} as received. This is a financial record.',
  },
  'approvalDetail.payment.markedPaidToast': { am: 'ክፍያው ተመዝግቧል', en: 'Payment recorded' },

  // --- Payment covering-letter (migration 029): editable free text,
  // prefilled from a settings default, printed on the payment-request PDF.
  // Not page-prefixed — components/PaymentLetterForm.jsx is the identical
  // control on both the admin approval-detail payment section and the sales
  // quotation-detail payment block.
  'paymentLetter.title': { am: 'የክፍያ ጥያቄ ደብዳቤ', en: 'Payment request letter' },
  'paymentLetter.label': { am: 'የደብዳቤው ጽሑፍ', en: 'Letter body' },
  'paymentLetter.save': { am: 'አስቀምጥ', en: 'Save' },
  'paymentLetter.saving': { am: 'በማስቀመጥ ላይ...', en: 'Saving...' },
  'paymentLetter.savedToast': { am: 'ደብዳቤው ተቀምጧል', en: 'Letter saved' },
  'paymentLetter.lockedNote': {
    am: 'ክፍያው እንደተከፈለ ከተመዘገበ በኋላ ደብዳቤው መቀየር አይቻልም።',
    en: 'Locked once the payment is marked paid — the letter can no longer be edited.',
  },

  // --- Issue access link — re-mint a tracking link for a customer who lost
  // theirs. Only valid once the quotation has been sent at least once.
  'approvalDetail.accessLink.title': { am: 'የመከታተያ ማገናኛ', en: 'Access link' },
  'approvalDetail.accessLink.hint': {
    am: 'ደንበኛው የመከታተያ ማገናኛውን ካጣ አዲስ ማገናኛ ይስጡ። የቀድሞ ማገናኛዎች ተቀባይነታቸው አይቀርም።',
    en: 'Issue a fresh link if the customer lost their tracking link. Existing links stay valid.',
  },
  'approvalDetail.accessLink.issue': { am: 'ማገናኛ ስጥ', en: 'Issue link' },
  'approvalDetail.accessLink.reissue': { am: 'ሌላ ማገናኛ ስጥ', en: 'Issue another link' },
  'approvalDetail.accessLink.issuing': { am: 'በመስጠት ላይ...', en: 'Issuing...' },
  'approvalDetail.accessLink.issuedToast': { am: 'አዲስ ማገናኛ ተሰጥቷል', en: 'New link issued' },

  // --- Sealed-bid tender (migration 020): always-visible section, not
  // behind the document-access-log Collapsible — decision-relevant for an
  // admin reviewing/approving a tender.
  'approvalDetail.seal.title': { am: 'የታሸገ ጨረታ', en: 'Sealed tender' },
  'approvalDetail.seal.status': { am: 'ሁኔታ', en: 'Status' },
  'approvalDetail.seal.statusSealed': {
    am: 'ታሽጓል — ገዥው ገና ሙሉ ይዘቱን አላየም',
    en: 'Sealed — the buyer cannot see the full content yet',
  },
  'approvalDetail.seal.statusUnsealed': { am: 'ተከፍቷል', en: 'Unsealed' },
  'approvalDetail.seal.sealedAt': { am: 'የታሸገበት ቀን', en: 'Sealed at' },
  'approvalDetail.seal.bidOpeningAt': { am: 'የታቀደ የጨረታ መክፈቻ', en: 'Scheduled bid opening' },
  'approvalDetail.seal.firstOpenedAt': { am: 'መጀመሪያ የተከፈተበት ጊዜ', en: 'First opened at' },
  'approvalDetail.seal.firstOpenedNone': {
    am: 'እስካሁን በማንም አልተከፈተም',
    en: 'Not opened by anyone yet',
  },
  'approvalDetail.seal.unsealedAt': { am: 'የተከፈተበት ቀን', en: 'Unsealed at' },
  'approvalDetail.seal.unsealedBy': { am: 'የከፈተው', en: 'Unsealed by' },
  'approvalDetail.seal.unsealRoute': { am: 'የመክፈቻ መንገድ', en: 'Unseal route' },
  'approvalDetail.seal.route.manual': { am: 'በእጅ', en: 'Manual' },
  'approvalDetail.seal.route.scheduled': { am: 'በራስ-ሰር የጊዜ ሰሌዳ', en: 'Scheduled' },
  'approvalDetail.seal.systemScheduled': { am: 'ስርዓት (በጊዜ ሰሌዳ)', en: 'System (scheduled)' },
  'approvalDetail.seal.unknownActor': { am: 'ያልታወቀ', en: 'Unknown' },
  'approvalDetail.seal.pendingSendNote': {
    am: 'ገና ማጽደቅ በመጠበቅ ላይ ነው። ማቅረቢያው ተጽድቆ ለገዥው ከተላከ በኋላ መክፈት ይቻላል።',
    en: 'Still awaiting approval. Unsealing becomes available once this proforma is approved and sent to the buyer.',
  },
  'approvalDetail.seal.unsealNow': { am: 'አሁን ክፈት', en: 'Unseal now' },
  'approvalDetail.seal.unsealing': { am: 'በመክፈት ላይ...', en: 'Unsealing...' },
  'approvalDetail.seal.unsealedToast': { am: 'ማቅረቢያው ተከፍቷል', en: 'Proforma unsealed' },
  'approvalDetail.seal.confirmUnsealTitle': { am: 'ይህ ማቅረቢያ ይከፈት?', en: 'Unseal this proforma?' },
  'approvalDetail.seal.confirmUnsealBody': {
    am: 'ገዥው ወዲያውኑ ሙሉ መስመሮችን እና ድምር ዋጋውን ያያል። ይህ ተመልሶ ሊታሸግ አይችልም።',
    en: 'The buyer will immediately see the full line items and total. It cannot be resealed.',
  },

  // --- Errors mapped from API error codes — admin/sales endpoints only
  // (see routes/*.js's sendIfKnown*Error() helpers for the mapping). Portal
  // never calls any of these endpoints, so it never receives these codes —
  // see shared/i18n/portal.js for the disjoint set of codes portal's own
  // guest/account routes can actually throw. -----------------------------
  'error.REQUEST_NOT_FOUND': {
    am: 'ይህ ጥያቄ አልተገኘም ወይም ለእርስዎ አልተመደበም።',
    en: 'This request was not found or is not assigned to you.',
  },
  'error.REQUEST_NOT_PRICEABLE': {
    am: 'ይህ ጥያቄ በአሁኑ ሁኔታ ዋጋ ሊሰጠው አይችልም።',
    en: 'This request cannot be priced in its current state.',
  },

  // --- Workspace attachment / column-mapping endpoints -------------------
  'error.UPLOAD_NOT_FOUND': {
    am: 'ይህ የተያያዘ ፋይል አልተገኘም።',
    en: 'This attachment was not found.',
  },
  'error.UPLOAD_NOT_READY': {
    am: 'ይህ ፋይል ገና ለማዛመድ ዝግጁ አይደለም።',
    en: 'This file is not ready to map yet.',
  },
  'error.UPLOAD_ALREADY_PROCESSED': {
    am: 'ይህ ፋይል አስቀድሞ ተከናውኗል። መስመሮቹን በቀጥታ በስራ ቦታው ያስተካክሉ።',
    en: 'This file has already been processed. Edit the line items directly in the workspace.',
  },
  'error.FILE_NOT_AVAILABLE': {
    am: 'የፋይሉ ይዘት አልተገኘም።',
    en: 'The file could not be found.',
  },
  'error.QUOTATION_NOT_EDITABLE': {
    am: 'ይህ የዋጋ ማቅረቢያ ከዚህ በኋላ ሊስተካከል አይችልም።',
    en: 'This quotation can no longer be edited.',
  },
  'error.EMPTY_QUOTATION': {
    am: 'ለማስገባት ቢያንስ አንድ የተሰጠ ዋጋ ያለው መስመር ያስፈልጋል።',
    en: 'At least one priced line is required.',
  },
  'error.BELOW_FLOOR_REASON_REQUIRED': {
    am: 'ከዝቅተኛ ዋጋ በታች የተሰጠ መስመር የቅናሽ ምክንያት ያስፈልገዋል።',
    en: 'A line priced below the floor price needs a discount reason.',
  },
  'error.FORBIDDEN': { am: 'ይህን ለማድረግ ፍቃድ የለዎትም።', en: 'You do not have permission to do that.' },
  'error.UNAUTHENTICATED': { am: 'መጀመሪያ መግባት ያስፈልግዎታል።', en: 'You need to log in first.' },
  'error.SESSION_EXPIRED': {
    am: 'ክፍለ ጊዜዎ አልቋል። እባክዎ እንደገና ይግቡ።',
    en: 'Your session has expired. Please log in again.',
  },
  'error.LOGIN_RATE_LIMITED': {
    am: 'በጣም ብዙ ያልተሳኩ ሙከራዎች። እባክዎ ቆየት ብለው እንደገና ይሞክሩ።',
    en: 'Too many failed attempts. Try again later.',
  },
  'error.TWO_FACTOR_INVALID': { am: 'ኮዱ ትክክል አልነበረም።', en: 'That code was not valid.' },
  'error.TWO_FACTOR_RATE_LIMITED': {
    am: 'በጣም ብዙ ሙከራዎች። እባክዎ ቆየት ብለው እንደገና ይሞክሩ።',
    en: 'Too many attempts. Try again later.',
  },
  'error.BANK_ACCOUNT_NOT_FOUND': {
    am: 'ይህ የባንክ ሒሳብ አልተገኘም።',
    en: 'This bank account was not found.',
  },
  'error.QUOTATION_NOT_FOUND': { am: 'ይህ የዋጋ ማቅረቢያ አልተገኘም።', en: 'This quotation was not found.' },
  'error.QUOTATION_NOT_SENT': {
    am: 'ይህ የዋጋ ማቅረቢያ ገና ስላልተላከ የመከታተያ ማገናኛ ሊሰጠው አይችልም።',
    en: 'This quotation has not been sent yet, so no access link can be issued.',
  },
  'error.PAYMENT_REQUEST_NOT_FOUND': {
    am: 'ለዚህ የዋጋ ማቅረቢያ ገና የክፍያ ጥያቄ የለም።',
    en: 'No payment request exists for this quotation yet.',
  },
  'error.PAYMENT_REQUEST_ALREADY_PAID': {
    am: 'ይህ ክፍያ አስቀድሞ እንደተከፈለ ተመዝግቧል።',
    en: 'This payment has already been recorded as paid.',
  },
  'error.PAYMENT_REQUEST_LOCKED': {
    am: 'ክፍያው ከተከፈለ በኋላ ደብዳቤው መቀየር አይቻልም።',
    en: 'This payment is already paid; its letter can no longer be edited.',
  },
  'error.QUOTATION_LINE_NOT_FOUND': {
    am: 'ይህ የመስመር ንጥል አልተገኘም።',
    en: 'This line item was not found.',
  },
  'error.QUOTATION_NOT_PENDING_APPROVAL': {
    am: 'ይህ የዋጋ ማቅረቢያ ማጽደቅ በሚጠብቅ ሁኔታ ላይ የለም።',
    en: 'This quotation is no longer awaiting approval.',
  },
  'error.INVALID_ASSIGNEE': {
    am: 'ይህ ተጠቃሚ ንቁ የሽያጭ ሰራተኛ አይደለም ወይም አልተገኘም።',
    en: 'That user is not an active sales account, or was not found.',
  },
  'error.REQUEST_NOT_ASSIGNABLE': {
    am: 'ይህ ጥያቄ በአሁኑ ሁኔታው መመደብ ወይም እንደገና መመደብ አይችልም።',
    en: 'This request can no longer be assigned or reassigned in its current state.',
  },
  'error.LINE_NOT_BELOW_FLOOR': {
    am: 'ይህ መስመር ከዝቅተኛ ዋጋ በታች አይደለም ወይም አስቀድሞ ተጽድቷል።',
    en: 'This line is not below floor, or has already been cleared.',
  },
  'error.UNCLEARED_FLOOR_LINES': {
    am: 'ከዝቅተኛ ዋጋ በታች ያሉ መስመሮች ገና አልጸደቁም። ከማጽደቅዎ በፊት እያንዳንዱን ያጽድቁ።',
    en: 'Below-floor lines are still uncleared. Clear each one before approving.',
  },
  'error.BELOW_FLOOR_EDIT_REJECTED': {
    am: 'የተስተካከለው ዋጋ ከዝቅተኛ ዋጋ በታች ነው። ከወለል በታች ማስተካከል በዋጋ አሰጣጡ ገጽ ላይ ከምክንያት ጋር ይደረጋል።',
    en: 'That price is below the floor. Below-floor pricing is done in the workspace with a reason, not on the approval screen.',
  },
  'error.EMPTY_REJECTION_REASON': {
    am: 'ውድቅ ለማድረግ ምክንያት ያስፈልጋል።',
    en: 'A reason is required to reject.',
  },
  'error.SEAL_SCHEDULE_INVALID': {
    am: 'የጨረታ መክፈቻ ጊዜ ትክክለኛ የወደፊት ጊዜ አልነበረም።',
    en: 'The bid opening time was not a valid future time.',
  },
  'error.QUOTATION_NOT_SEALED': {
    am: 'ይህ የዋጋ ማቅረቢያ ታሽጎ አያውቅም ወይም ገና አልተላከም።',
    en: 'This quotation was never sealed, or has not been sent yet.',
  },
  'error.QUOTATION_ALREADY_UNSEALED': {
    am: 'ይህ ማቅረቢያ አስቀድሞ ተከፍቷል፤ ዳግም ሊታሸግ አይችልም።',
    en: 'This proforma has already been unsealed and cannot be resealed.',
  },

  'error.CATEGORY_NOT_FOUND': { am: 'ይህ ምድብ አልተገኘም።', en: 'This category was not found.' },
  'error.CATEGORY_HAS_DEPENDENTS': {
    am: 'ይህ ምድብ ምርቶች ወይም ንዑስ ምድቦች ስላሉት ሊሰረዝ አይችልም። በምትኩ ያሰናብቱት።',
    en: 'This category has products or subcategories and cannot be deleted. Deactivate it instead.',
  },
  'error.CATEGORY_ALREADY_INACTIVE': {
    am: 'ይህ ምድብ አስቀድሞ ታግዷል።',
    en: 'This category is already inactive.',
  },
  'error.CATEGORY_ATTRIBUTE_NOT_FOUND': {
    am: 'ይህ ባህሪ አልተገኘም።',
    en: 'This attribute was not found.',
  },
  'error.CATEGORY_ATTRIBUTE_ALREADY_INACTIVE': {
    am: 'ይህ ባህሪ አስቀድሞ ታግዷል።',
    en: 'This attribute is already inactive.',
  },
  'error.DUPLICATE_ATTRIBUTE_KEY': {
    am: 'በዚህ ምድብ ውስጥ በዚህ ቁልፍ ያለ ባህሪ አስቀድሞ አለ።',
    en: 'An attribute with this key already exists on this category.',
  },
  'error.PRODUCT_NOT_FOUND': { am: 'ይህ ምርት አልተገኘም።', en: 'This product was not found.' },
  'error.PRODUCT_ALREADY_INACTIVE': {
    am: 'ይህ ምርት አስቀድሞ ታቦዟል።',
    en: 'This product is already inactive.',
  },
  'error.PRODUCT_ALREADY_ACTIVE': {
    am: 'ይህ ምርት አስቀድሞ ንቁ ነው።',
    en: 'This product is already active.',
  },
  'error.INVALID_IMAGE_TYPE': {
    am: 'JPEG፣ PNG ወይም WebP ፋይል ብቻ ተቀባይነት አለው።',
    en: 'Only JPEG, PNG, or WebP files are accepted.',
  },
  'error.FILE_TOO_LARGE': { am: 'ፎቶው ከ8ሜባ መብለጥ አይችልም።', en: 'The photo cannot exceed 8MB.' },
  'error.FILE_REQUIRED': { am: 'የፎቶ ፋይል ያስፈልጋል።', en: 'An image file is required.' },
  'error.UPLOAD_ERROR': {
    am: 'ፎቶውን መስቀል አልተቻለም። እባክዎ እንደገና ይሞክሩ።',
    en: 'Could not upload the photo. Please try again.',
  },

  'error.FLOOR_EXCEEDS_LIST': {
    am: 'ዝቅተኛ ዋጋ ከዝርዝር ዋጋ መብለጥ አይችልም።',
    en: 'Floor price cannot exceed list price.',
  },
  'error.DUPLICATE_SKU': {
    am: 'ይህ ኮድ (SKU) አስቀድሞ ጥቅም ላይ ውሏል። ሌላ ኮድ ይምረጡ።',
    en: 'That SKU is already in use. Choose a different one.',
  },
  'error.FEATURED_LIMIT_EXCEEDED': {
    am: 'ቢበዛ 8 ምርቶች ብቻ በአንድ ጊዜ ሊደምቁ ይችላሉ። መጀመሪያ ሌላ ምርት ከማድመቅ ያንሱ።',
    en: 'At most 8 products can be featured at once. Unfeature another product first.',
  },
  'error.UNKNOWN_ATTRIBUTE_KEY': {
    am: 'ከቀረቡት ባህሪያት አንዱ ለዚህ ምድብ ትክክለኛ አይደለም።',
    en: 'One of the submitted attributes is not valid for this product’s category.',
  },
  'error.DUPLICATE_BRAND_NAME': {
    am: 'ይህ ብራንድ ስም አስቀድሞ አለ።',
    en: 'That brand name already exists.',
  },
  'error.PRODUCT_BRAND_NOT_FOUND': {
    am: 'ይህ ብራንድ አልተገኘም።',
    en: 'This brand was not found.',
  },
  // ታግዷል didn't match this entity's OWN term — found in the native-speaker
  // review pass logged in PROGRESS.md. categories.brands.deactivatedToast
  // (below) already calls a deactivated brand ተሰናክሏል, so this sibling
  // "already inactive" error should say the same thing about the same
  // brand, not borrow categories/attributes' own separate ታግዷል convention
  // (categories.deactivatedToast, categories.attr.deactivatedToast) — this
  // codebase is internally consistent per entity type, just not unified
  // across types, so the fix here is to match THIS entity, not to purge
  // ታግዷል everywhere.
  'error.PRODUCT_BRAND_ALREADY_INACTIVE': {
    am: 'ይህ ብራንድ አስቀድሞ ተሰናክሏል።',
    en: 'This brand is already inactive.',
  },

  'error.DUPLICATE_EMAIL': {
    am: 'ይህ ኢሜይል አስቀድሞ ጥቅም ላይ ውሏል። ሌላ ኢሜይል ይሞክሩ።',
    en: 'That email is already in use. Try a different one.',
  },
  'error.USER_NOT_FOUND': { am: 'ይህ ተጠቃሚ አልተገኘም።', en: 'This user was not found.' },
  'error.USER_ALREADY_INACTIVE': {
    am: 'ይህ ተጠቃሚ አስቀድሞ ቦዝኗል።',
    en: 'This user is already inactive.',
  },
  'error.USER_ALREADY_ACTIVE': {
    am: 'ይህ ተጠቃሚ አስቀድሞ ንቁ ነው።',
    en: 'This user is already active.',
  },
  'error.CANNOT_ACT_ON_SELF': {
    am: 'የራስዎን መለያ ማቦዘን ወይም መሰረዝ አይችሉም።',
    en: 'You cannot deactivate or delete your own account.',
  },
  'error.LAST_ACTIVE_ADMIN': {
    am: 'የመጨረሻው ንቁ አስተዳዳሪ ስለሆነ ሊነሳ አይችልም።',
    en: 'This is the last remaining active admin and cannot be removed.',
  },
  'error.BULK_DELETE_CONFIRMATION_MISMATCH': {
    am: 'የተተየበው ቁጥር ከተመረጡት ጋር አይመሳሰልም።',
    en: 'The typed count does not match the selection.',
  },
  'error.ORGANIZATION_NOT_FOUND': { am: 'ይህ ደንበኛ አልተገኘም።', en: 'This customer was not found.' },
  'error.ORGANIZATION_ALREADY_INACTIVE': {
    am: 'ይህ ደንበኛ አስቀድሞ ቦዝኗል።',
    en: 'This customer is already deactivated.',
  },
  'error.ORGANIZATION_ALREADY_ACTIVE': {
    am: 'ይህ ደንበኛ አስቀድሞ ንቁ ነው።',
    en: 'This customer is already active.',
  },
  'error.UNKNOWN_PERMISSION': {
    am: 'ይህ ፈቃድ አይታወቅም።',
    en: 'That is not a known permission.',
  },
  // Per-permission gates (utils/permissionCatalog.js) on the sales
  // unseal/re-quote/assign actions.
  'error.UNSEAL_NOT_PERMITTED': {
    am: 'ይህን ማቅረቢያ የመክፈት ፈቃድ የለዎትም። አስተዳዳሪ ይጠይቁ።',
    en: 'You do not have permission to unseal this proforma. Ask an admin.',
  },
  'error.REQUOTE_NOT_PERMITTED': {
    am: 'ይህን ማቅረቢያ ዳግም የማቅረብ ፈቃድ የለዎትም። አስተዳዳሪ ይጠይቁ።',
    en: 'You do not have permission to re-quote this proforma. Ask an admin.',
  },
  'error.ASSIGN_NOT_PERMITTED': {
    am: 'ጥያቄዎችን የመመደብ ፈቃድ የለዎትም። አስተዳዳሪ ይጠይቁ።',
    en: 'You do not have permission to assign requests. Ask an admin.',
  },
  'error.QUOTATION_NOT_REQUOTABLE': {
    am: 'ይህ ማቅረቢያ አላለቀም፣ ስለዚህ ዳግም ሊቀርብ አይችልም።',
    en: 'This proforma is not expired, so it cannot be re-quoted.',
  },

  // --- Sales "Today" (nav.sales.today) — a daily task list combining four
  // signals for one salesperson's own requests: unpriced-over-threshold,
  // rejected-needing-repricing, expiring-soon, near-needed-by. See
  // services/requestService.js#getTodayForSalesUser.
  'nav.sales.today': { am: 'ዛሬ', en: 'Today' },
  'nav.sales.myQueue': { am: 'የእኔ ወረፋ', en: 'My queue' },
  'nav.sales.myQuotations': { am: 'የእኔ ማቅረቢያዎች', en: 'My quotations' },

  'today.title': { am: 'ዛሬ', en: 'Today' },
  'today.subtitle': {
    am: 'አሁን ትኩረት የሚያስፈልጋቸው የእርስዎ ጥያቄዎች።',
    en: 'Your requests that need attention right now.',
  },
  'today.errorBody': { am: 'የዛሬ ስራዎችን መጫን አልተቻለም።', en: "Could not load today's tasks." },
  'today.allClearTitle': { am: 'ትኩረት የሚያስፈልገው ነገር የለም', en: 'Nothing needs attention' },
  'today.allClearBody': {
    am: 'ለእርስዎ የተመደቡት ስራዎች ሁሉ በትክክለኛ ሁኔታ ላይ ናቸው።',
    en: 'Everything assigned to you is on track.',
  },
  'today.section.unpriced.title': {
    am: 'ከ{hours} ሰዓት በላይ ዋጋ ያልተሰጣቸው',
    en: 'Unpriced over {hours}h',
  },
  'today.section.unpriced.empty': { am: 'የለም።', en: 'None.' },
  'today.section.rejected.title': {
    am: 'ውድቅ የተደረገ፣ ዳግም ዋጋ ያስፈልገዋል',
    en: 'Rejected, needs re-pricing',
  },
  'today.section.rejected.empty': { am: 'የለም።', en: 'None.' },
  'today.section.expiring.title': {
    am: 'በ{days} ቀናት ውስጥ የሚያልቅ',
    en: 'Expiring within {days} days',
  },
  'today.section.expiring.empty': { am: 'የለም።', en: 'None.' },
  'today.section.neededBySoon.title': {
    am: 'የማድረሻ ቀን እየተቃረበ ነው',
    en: 'Delivery date approaching',
  },
  'today.section.neededBySoon.empty': { am: 'የለም።', en: 'None.' },

  // --- Sales "My quotations" (nav.sales.myQuotations) — every quotation
  // this salesperson has priced. See
  // services/quotationService.js#listForSalesUser/getForSalesDetail.
  'myQuotations.title': { am: 'የእኔ ማቅረቢያዎች', en: 'My quotations' },
  'myQuotations.subtitle': {
    am: 'እርስዎ ዋጋ የሰጡባቸው ሁሉም ማቅረቢያዎች።',
    en: 'Every quotation you have priced.',
  },
  'myQuotations.filter.status': { am: 'ሁኔታ', en: 'Status' },
  'myQuotations.filter.all': { am: 'ሁሉም', en: 'All' },
  'myQuotations.col.proforma': { am: 'ማቅረቢያ', en: 'Proforma' },
  'myQuotations.col.organization': { am: 'ድርጅት', en: 'Organization' },
  'myQuotations.col.status': { am: 'ሁኔታ', en: 'Status' },
  'myQuotations.col.seal': { am: 'ማህተም', en: 'Seal' },
  'myQuotations.col.total': { am: 'ጠቅላላ', en: 'Total' },
  'myQuotations.col.validUntil': { am: 'የሚያልቅበት ቀን', en: 'Valid until' },
  'myQuotations.seal.sealed': { am: 'የታሸገ', en: 'Sealed' },
  'myQuotations.seal.unsealed': { am: 'ተከፍቷል', en: 'Unsealed' },
  'myQuotations.emptyTitle': { am: 'እስካሁን ምንም ማቅረቢያ የለም', en: 'No quotations yet' },
  'myQuotations.emptyBody': {
    am: 'እርስዎ ዋጋ ሰጥተው የሚያስገቧቸው ማቅረቢያዎች እዚህ ይታያሉ።',
    en: 'Quotations you price and submit will appear here.',
  },
  'myQuotations.errorBody': {
    am: 'የእርስዎን ማቅረቢያዎች መጫን አልተቻለም።',
    en: 'Could not load your quotations.',
  },

  // --- Sales quotation detail (pages/SalesQuotationDetailPage.jsx) —
  // read-only document + the permission-gated actions a salesperson may
  // take: unseal (reuses approvalDetail.seal.* strings below, same
  // continuity reasoning as components/SealSection.jsx), re-quote, add a
  // note.
  'salesQuotationDetail.back': { am: '← ወደ የእኔ ማቅረቢያዎች ተመለስ', en: '← Back to my quotations' },
  'salesQuotationDetail.errorBody': {
    am: 'ይህን ማቅረቢያ መጫን አልተቻለም።',
    en: 'Could not load this quotation.',
  },
  'salesQuotationDetail.actionsTitle': { am: 'ተግባራት', en: 'Actions' },
  'salesQuotationDetail.requote.title': { am: 'ዳግም ዋጋ መስጠት', en: 'Re-quote' },
  'salesQuotationDetail.requote.hint': {
    am: 'ይህ ማቅረቢያ አልቋል። በአሁኑ ዋጋዎች አዲስ ማቅረቢያ ይፍጠሩ።',
    en: 'This proforma has expired. Generate a new one at current prices.',
  },
  'salesQuotationDetail.requote.button': { am: 'አሁን ዳግም ዋጋ ስጥ', en: 'Re-quote now' },
  'salesQuotationDetail.requote.requoting': { am: 'ዳግም በመስጠት ላይ...', en: 'Re-quoting...' },
  'salesQuotationDetail.requote.confirmTitle': {
    am: 'ይህ ማቅረቢያ ዳግም ይሰጥ?',
    en: 'Re-quote this proforma?',
  },
  'salesQuotationDetail.requote.confirmBody': {
    am: 'ይህ በአሁኑ ዋጋዎች አዲስ ክለሳ ይፈጥራል እና ጥያቄውን ወደ እርስዎ የዋጋ አሰጣጥ ስራ ቦታ ይመልሳል።',
    en: 'This creates a new revision at current prices and returns the request to your pricing workspace.',
  },
  'salesQuotationDetail.requote.successToast': {
    am: 'ዳግም ዋጋ ተሰጥቷል — ወረፋዎን ይመልከቱ።',
    en: 'Re-quoted — check your queue.',
  },
  'salesQuotationDetail.note.title': { am: 'ማስታወሻ ጨምር', en: 'Add a note' },
  'salesQuotationDetail.note.placeholder': {
    am: 'ስለዚህ ማቅረቢያ ማስታወሻ ይጻፉ...',
    en: 'Write a note about this quotation...',
  },
  'salesQuotationDetail.note.button': { am: 'ማስታወሻ ጨምር', en: 'Add note' },
  'salesQuotationDetail.note.adding': { am: 'በመጨመር ላይ...', en: 'Adding...' },
  'salesQuotationDetail.note.successToast': { am: 'ማስታወሻ ተጨምሯል', en: 'Note added' },
  'salesQuotationDetail.note.emptyError': {
    am: 'እባክዎ መጀመሪያ የሆነ ነገር ይጻፉ።',
    en: 'Write something first.',
  },

  // --- "My queue" reassign action (can_assign permission).
  'queue.assign.button': { am: 'እንደገና መድብ', en: 'Reassign' },
  'queue.assign.confirmTitle': { am: 'ይህ ጥያቄ እንደገና ይመደብ?', en: 'Reassign this request?' },
  'queue.assign.confirmBody': {
    am: 'ጥያቄው ወደ ተመረጠው የሽያጭ ሰራተኛ ወረፋ ይዛወራል።',
    en: "The request will move to the selected salesperson's queue.",
  },
  'queue.assign.successToast': { am: 'ጥያቄው እንደገና ተመድቧል', en: 'Request reassigned' },
  'queue.assign.selectPlaceholder': { am: 'የሽያጭ ሰራተኛ ይምረጡ...', en: 'Choose a salesperson...' },

  // --- Signature element (shared/components/SignatureElement) — the only
  // consumer today is admin/ (AppShell's header, WorkspacePage,
  // ApprovalDetailPage); portal has no screen that reaches an
  // approved/stamped quotation yet, see PROGRESS.md. -----------------------
  'signature.proformaNumber': { am: 'የፕሮፎርማ ቁጥር', en: 'Proforma number' },
  'signature.status.unallocated': { am: 'ገና ቁጥር አልተመደበም', en: 'Not yet allocated' },
  'signature.status.approved': { am: 'ጸድቋል', en: 'Approved' },
  'signature.status.stamped': { am: 'ታትሞ ተልኳል', en: 'Stamped and sent' },
  'signature.status.revised': { am: 'ተሻሽሏል', en: 'Revised, superseded' },
  'signature.legend.title': { am: 'የደረጃ መግለጫ', en: 'Status legend' },
  'signature.legend.unallocated': {
    am: 'የተቆራረጠ ግራጫ መስመር — ገና ቁጥር አልተመደበም',
    en: 'Dashed grey — no number allocated yet',
  },
  'signature.legend.approved': { am: 'ጠንካራ ወይንጠጅ መስመር — ጸድቋል', en: 'Solid violet — approved' },
  'signature.legend.stamped': {
    am: 'ድርብ ወይንጠጅ መስመር — ታትሞ ተልኳል',
    en: 'Doubled violet — stamped and sent',
  },
  'signature.legend.revised': {
    am: 'ጠንካራ ብርቱካናማ መስመር — ተሻሽሏል',
    en: 'Solid amber — revised, superseded',
  },

  // --- Multi-select bulk actions (customers/products/users task) --------
  // Shared across CustomersPage/CatalogPage/UsersPage — nothing
  // resource-specific belongs in this block, see components/
  // BulkActionBar.jsx and utils/bulkActions.js.
  'bulk.barLabel': { am: 'የጅምላ ተግባራት', en: 'Bulk actions' },
  'bulk.selectAll': { am: 'ሁሉንም ምረጥ', en: 'Select all' },
  'bulk.selectedCount': { am: '{count} ተመርጠዋል', en: '{count} selected' },
  'bulk.clearSelection': { am: 'ምርጫ አጽዳ', en: 'Clear selection' },
  'bulk.deactivate': { am: 'አቦዝን', en: 'Deactivate' },
  'bulk.reactivate': { am: 'እንደገና አንቃ', en: 'Reactivate' },
  'bulk.delete': { am: 'ሰርዝ', en: 'Delete' },
  'bulk.actionDoneToast': {
    am: '{ok} ተከናውኗል፣ {skipped} ታልፏል',
    en: '{ok} done, {skipped} skipped',
  },
  'bulk.deactivateConfirmTitle': {
    am: '{count} ንጥሎችን አቦዝን?',
    en: 'Deactivate {count} item(s)?',
  },
  'bulk.deactivateConfirmBody': {
    am: 'የተመረጡት ንጥሎች ይቦዘናሉ። ይህን በኋላ መመለስ ይቻላል።',
    en: 'The selected items will be deactivated. This can be reversed later.',
  },
  'bulk.deactivateConfirmButton': { am: 'አቦዝን', en: 'Deactivate' },
  'bulk.reactivateConfirmTitle': {
    am: '{count} ንጥሎችን እንደገና አንቃ?',
    en: 'Reactivate {count} item(s)?',
  },
  'bulk.reactivateConfirmBody': {
    am: 'የተመረጡት ንጥሎች እንደገና ንቁ ይሆናሉ።',
    en: 'The selected items will become active again.',
  },
  'bulk.reactivateConfirmButton': { am: 'እንደገና አንቃ', en: 'Reactivate' },
  'bulk.delete.confirmTitle': { am: '{count} ንጥሎችን ሰርዝ?', en: 'Delete {count} item(s)?' },
  'bulk.delete.confirmButton': { am: 'ሰርዝ', en: 'Delete' },
  'bulk.delete.typeCountLabel': {
    am: 'ለማረጋገጥ {count} ብለው ይተይቡ',
    en: 'Type {count} to confirm',
  },
  'bulk.delete.previewDeletePart': {
    am: '{count} ሙሉ በሙሉ ይሰረዛሉ።',
    en: '{count} will be permanently deleted.',
  },
  'bulk.delete.previewDeactivatePart': {
    am: '{count} ገና ስለሚያገለግሉ ይቦዘናሉ እንጂ አይሰረዙም።',
    en: '{count} are still referenced elsewhere and will be deactivated instead.',
  },
  'bulk.delete.previewSkipPart': {
    am: '{count} ይታለፋሉ (መሰረዝ/ማቦዘን አይቻልም)።',
    en: '{count} will be skipped (cannot be acted on).',
  },

  // --- Customers (organizations) — new admin nav item --------------------
  'nav.admin.customers': { am: 'ደንበኞች', en: 'Customers' },
  'customers.title': { am: 'ደንበኞች', en: 'Customers' },
  'customers.subtitle': {
    am: 'ጥያቄ ካቀረቡ ድርጅቶች ሁሉ — እንግዳ እና የተመዘገቡ።',
    en: 'Every organization that has submitted a request — guest and registered.',
  },
  'customers.searchPlaceholder': { am: 'በስም፣ ቲን ወይም ኢሜይል ፈልግ', en: 'Search name, TIN, or email' },
  'customers.col.name': { am: 'ድርጅት', en: 'Organization' },
  'customers.col.tin': { am: 'ቲን', en: 'TIN' },
  'customers.col.tier': { am: 'ደረጃ', en: 'Tier' },
  'customers.col.status': { am: 'ሁኔታ', en: 'Status' },
  'customers.col.contact': { am: 'የሚገናኝ ሰው', en: 'Contact' },
  'customers.col.email': { am: 'ኢሜይል', en: 'Email' },
  'customers.col.phone': { am: 'ስልክ', en: 'Phone' },
  'customers.col.requests': { am: 'ጥያቄዎች', en: 'Requests' },
  'customers.col.quotations': { am: 'ፕሮፎርማዎች', en: 'Quotations' },
  'customers.col.valueWon': { am: 'የተገኘ ዋጋ', en: 'Value won' },
  'customers.col.lastActivity': { am: 'መጨረሻ እንቅስቃሴ', en: 'Last activity' },
  'customers.status.guest': { am: 'እንግዳ', en: 'Guest' },
  'customers.status.active': { am: 'ንቁ', en: 'Active' },
  'customers.status.suspended': { am: 'ታግዷል', en: 'Deactivated' },
  'customers.tier.government': { am: 'መንግስት', en: 'Government' },
  'customers.tier.ngo': { am: 'መንግስታዊ ያልሆነ', en: 'NGO' },
  'customers.tier.corporate': { am: 'ድርጅት', en: 'Corporate' },
  'customers.tier.retail': { am: 'ችርቻሮ', en: 'Retail' },
  'customers.neverActive': { am: 'እንቅስቃሴ የለም', en: 'No activity yet' },
  'customers.emptyTitle': { am: 'ገና ምንም ደንበኛ የለም', en: 'No customers yet' },
  'customers.emptyBody': {
    am: 'ድርጅቶች የመጀመሪያ ጥያቄያቸውን ሲያቀርቡ እዚህ ይታያሉ።',
    en: 'Organizations show up here once they submit their first request.',
  },
  'customers.errorBody': { am: 'ደንበኞችን መጫን አልተቻለም።', en: 'Could not load customers.' },
  'customers.backToList': { am: '← ወደ ደንበኞች ዝርዝር', en: '← Back to customers' },
  'customers.notFoundTitle': { am: 'ደንበኛ አልተገኘም', en: 'Customer not found' },

  'customers.detail.editButton': { am: 'አርትዕ', en: 'Edit' },
  'customers.detail.deactivateButton': { am: 'አቦዝን', en: 'Deactivate' },
  'customers.detail.reactivateButton': { am: 'እንደገና አንቃ', en: 'Reactivate' },
  'customers.detail.deleteButton': { am: 'ሰርዝ', en: 'Delete' },
  'customers.detail.requestsTitle': { am: 'ጥያቄዎች', en: 'Requests' },
  'customers.detail.requestsEmpty': { am: 'ገና ምንም ጥያቄ የለም።', en: 'No requests yet.' },
  'customers.detail.quotationsTitle': { am: 'ፕሮፎርማዎች', en: 'Quotations' },
  'customers.detail.quotationsEmpty': { am: 'ገና ምንም ፕሮፎርማ የለም።', en: 'No quotations yet.' },
  'customers.detail.contactsTitle': { am: 'የሚገናኙ ሰዎች', en: 'Contact people' },
  'customers.detail.contactsEmpty': {
    am: 'ይህ ድርጅት የተመዘገበ የገዥ መለያ የለውም።',
    en: 'This organization has no registered buyer account yet.',
  },

  'customers.form.nameLabel': { am: 'የድርጅት ስም', en: 'Organization name' },
  'customers.form.nameAmLabel': { am: 'ስም (አማርኛ)', en: 'Name (Amharic)' },
  'customers.form.tinLabel': { am: 'ቲን', en: 'TIN' },
  'customers.form.tierLabel': { am: 'ደረጃ', en: 'Tier' },
  'customers.form.contactNameLabel': { am: 'የሚገናኝ ሰው ስም', en: 'Contact name' },
  'customers.form.contactEmailLabel': { am: 'የመገናኛ ኢሜይል', en: 'Contact email' },
  'customers.form.contactPhoneLabel': { am: 'የመገናኛ ስልክ', en: 'Contact phone' },
  'customers.form.addressLabel': { am: 'አድራሻ', en: 'Address' },
  'customers.form.submit': { am: 'አስቀምጥ', en: 'Save' },
  'customers.form.submitting': { am: 'በማስቀመጥ ላይ...', en: 'Saving...' },
  'customers.form.cancel': { am: 'ይቅር', en: 'Cancel' },
  'customers.form.nameRequired': { am: 'የድርጅት ስም ያስፈልጋል።', en: 'Organization name is required.' },

  'customers.updatedToast': { am: 'ደንበኛ ተስተካክሏል', en: 'Customer updated' },
  'customers.deactivatedToast': { am: 'ደንበኛ ተቦዝኗል', en: 'Customer deactivated' },
  'customers.reactivatedToast': { am: 'ደንበኛ እንደገና ነቅቷል', en: 'Customer reactivated' },
  'customers.deletedToast': { am: 'ደንበኛ ተሰርዟል', en: 'Customer deleted' },
  'customers.deactivateConfirmTitle': { am: '{name}ን አቦዝን?', en: 'Deactivate {name}?' },
  'customers.deactivateConfirmBody': {
    am: 'ይህ ደንበኛ ይቦዘናል። ይህን በኋላ መመለስ ይቻላል።',
    en: 'This customer will be deactivated. This can be reversed later.',
  },
  'customers.reactivateConfirmTitle': { am: '{name}ን እንደገና አንቃ?', en: 'Reactivate {name}?' },
  'customers.deleteConfirmTitle': { am: '{name}ን ሰርዝ?', en: 'Delete {name}?' },
  'customers.deleteConfirmBodyWillDelete': {
    am: 'ምንም ጥያቄ ወይም ፕሮፎርማ ስለሌለው ሙሉ በሙሉ ይሰረዛል።',
    en: 'It has no requests or quotations and will be permanently deleted.',
  },
  'customers.deleteConfirmBodyWillDeactivate': {
    am: 'ጥያቄ ወይም ፕሮፎርማ ስላለው በምትኩ ይቦዘናል፣ አይሰረዝም።',
    en: 'It has requests or quotations and will be deactivated instead, not deleted.',
  },

  // --- Reactivate additions to existing catalog/users screens -----------
  'catalog.actions.reactivate': { am: 'እንደገና አንቃ', en: 'Reactivate' },
  'catalog.actions.reactivating': { am: 'በማንቃት ላይ...', en: 'Reactivating...' },
  'catalog.product.reactivatedToast': { am: 'ምርት እንደገና ነቅቷል', en: 'Product reactivated' },
  'catalog.filter.showInactive': { am: 'የቦዘኑትንም አሳይ', en: 'Show inactive too' },
  'users.actions.reactivate': { am: 'እንደገና አንቃ', en: 'Reactivate' },
  'users.actions.reactivating': { am: 'በማንቃት ላይ...', en: 'Reactivating...' },
  'users.reactivatedToast': { am: 'ተጠቃሚ እንደገና ነቅቷል', en: 'User reactivated' },
  'users.filter.showAdmins': { am: 'አስተዳዳሪዎችንም አሳይ', en: 'Show admins too' },
  'users.col.role': { am: 'ሚና', en: 'Role' },
  'users.role.admin': { am: 'አስተዳዳሪ', en: 'Admin' },
  'users.role.sales': { am: 'ሽያጭ', en: 'Sales' },
  // Third staff role (migration 025) — POST /admin/users now accepts
  // role: 'sales' | 'catalog_manager' (routes/adminUserRoutes.js's own
  // createUserSchema). Used both by this row-label lookup and by
  // UserForm.jsx's new role selector (users.form.role.* below reuses these
  // same two value strings rather than duplicating them).
  'users.role.catalog_manager': { am: 'ካታሎግ አያያዝ', en: 'Catalog manager' },

  // --- UserForm.jsx role selector (create-user form) ---------------------
  'users.form.createTitleGeneric': { am: 'አዲስ ሰራተኛ ጨምር', en: 'Add a new staff user' },
  'users.form.roleLabel': { am: 'ሚና', en: 'Role' },

  // --- catalog_manager "Incoming requests" list — route /incoming-requests
  // (GET /catalog-manager/requests). Ledger-style: ref_no/needed_by/
  // line_count only, no organization/status/assignee (this role never
  // receives those fields at all — see
  // routes/catalogManagerRequestRoutes.js's own header comment). ----------
  'catalogManagerRequests.title': { am: 'የገቡ ጥያቄዎች', en: 'Incoming requests' },
  'catalogManagerRequests.subtitle': {
    am: 'ደንበኞች የሚያስገቧቸው ጥያቄዎች፣ ምን እና ምን ያህል እንደተጠየቀ ብቻ።',
    en: 'What buyers are asking for and how much — nothing else.',
  },
  'catalogManagerRequests.col.ref': { am: 'የጥያቄ ቁጥር', en: 'Ref' },
  'catalogManagerRequests.col.neededBy': { am: 'የማድረሻ ቀን', en: 'Needed by' },
  'catalogManagerRequests.col.lines': { am: 'የዕቃ ብዛት', en: 'Lines' },
  'catalogManagerRequests.neededByNone': { am: 'አልተገለጸም', en: 'Not specified' },
  'catalogManagerRequests.openDetail': { am: 'የጥያቄውን ዝርዝር ክፈት', en: 'Open request detail' },
  // Zero incoming requests is a normal state, not a failure (root
  // CLAUDE.md) — this copy invites waiting for the next submission, it
  // does not apologise.
  'catalogManagerRequests.emptyTitle': { am: 'ገና ምንም የገባ ጥያቄ የለም', en: 'No incoming requests yet' },
  'catalogManagerRequests.emptyBody': {
    am: 'ደንበኛ ጥያቄ ባስገባ ቁጥር እዚህ ይታያል።',
    en: 'New requests will show up here as soon as a buyer submits one.',
  },
  'catalogManagerRequests.errorBody': {
    am: 'ጥያቄዎችን መጫን አልተቻለም።',
    en: 'Could not load incoming requests.',
  },

  // --- catalog_manager request detail — route
  // /incoming-requests/:requestId (GET /catalog-manager/requests/:id). Line-
  // level only: raw_description/raw_unit/qty + matched product id/name, plus
  // needed_by. No organization/customer identity, no quotation/pricing/
  // status data (this role's read never carries any of that). -------------
  'catalogManagerRequestDetail.back': { am: '← ወደ ጥያቄዎች ተመለስ', en: '← Back to requests' },
  'catalogManagerRequestDetail.readOnlyHint': {
    am: 'ይህ ዕይታ ለማንበብ ብቻ ነው። የደንበኛ መረጃ ወይም የዋጋ ማቅረቢያ መረጃ አያሳይም።',
    en: 'This view is read-only. It shows no customer identity or quotation data.',
  },
  'catalogManagerRequestDetail.header.neededBy': { am: 'የማድረሻ ቀን', en: 'Needed by' },
  'catalogManagerRequestDetail.header.neededByNone': { am: 'አልተገለጸም', en: 'Not specified' },
  'catalogManagerRequestDetail.lines.title': { am: 'የዕቃ ዝርዝር', en: 'Line items' },
  'catalogManagerRequestDetail.lines.col.line': { am: '#', en: '#' },
  'catalogManagerRequestDetail.lines.col.description': { am: 'መግለጫ', en: 'Description' },
  'catalogManagerRequestDetail.lines.col.unit': { am: 'መለኪያ', en: 'Unit' },
  'catalogManagerRequestDetail.lines.col.qty': { am: 'ብዛት', en: 'Qty' },
  'catalogManagerRequestDetail.lines.col.matched': { am: 'የካታሎግ ምርት', en: 'Catalog product' },
  'catalogManagerRequestDetail.lines.noMatch': {
    am: 'ገና ከካታሎግ ጋር አልተዛመደም',
    en: 'No catalog match yet',
  },
  'catalogManagerRequestDetail.lines.unitNone': { am: '—', en: '—' },
  'catalogManagerRequestDetail.lines.qtyNone': { am: '—', en: '—' },
  // Zero lines on a real request is a normal state, not a failure (root
  // CLAUDE.md's own "zero matched Excel lines" framing, applied here to
  // "zero lines at all").
  'catalogManagerRequestDetail.lines.emptyTitle': {
    am: 'ይህ ጥያቄ ገና ምንም የዕቃ መስመር የለውም',
    en: 'This request has no line items yet',
  },
  'catalogManagerRequestDetail.lines.emptyBody': {
    am: 'ይህ የተለመደ ነው — ጥያቄው ገና በመሰራት ላይ ያለ ሊሆን ይችላል።',
    en: 'That is normal — this request may still be in progress.',
  },
  'catalogManagerRequestDetail.errorBody': {
    am: 'ይህን ጥያቄ መጫን አልተቻለም።',
    en: 'Could not load this request.',
  },
};

export default strings;
